<template>
  <v-container
    fluid
    v-if="profile"
    class="job-dossier"
    :class="'job-dossier--' + jobDetailsViewMode"
  >
    <loading :active.sync="loading" />
    <v-alert v-if="error !== null" outline>{{ error }}</v-alert>

    <section class="jd-command jd-command--flat">
      <div class="jd-command__nav jd-command__nav--flat">
        <button type="button" class="jd-back" @click="goBackToJobs">
          <v-icon small>arrow_back</v-icon>
          Jobs
        </button>

        <div class="jd-job-strip">
          <strong>Job {{ profile.jobId || jobID }}</strong>
          <span
            class="jd-status-pill"
            :class="'jd-status-pill--' + jobStatusClass"
          >
            {{ jobStatusLabel }}
          </span>
          <small v-if="jobDateDisplay">{{ jobDateDisplay }}</small>
          <small v-if="customerDisplayName">{{ customerDisplayName }}</small>
        </div>

        <div
          v-if="jobDetailsViewModes.length > 1"
          class="jd-view-switch"
          aria-label="Job details view"
        >
          <button
            v-for="view in jobDetailsViewModes"
            :key="view.value"
            type="button"
            :class="{ active: jobDetailsViewMode === view.value }"
            @click="setJobDetailsViewMode(view.value)"
          >
            {{ view.label }}
          </button>
        </div>
      </div>
    </section>

    <div v-if="jobDetailsViewMode === 'modern'" class="jd-modern-workspace">
      <section class="jd-section jd-section--profile">
        <JobsProfileDetail
          :detail="profile"
          :workingcountry="profile.workingCountry"
          :userDetail="customerDetails"
          v-on:updateJobs="updateJobs"
          :viewOnly="viewOnly"
        />

        <ConfirmationDialog
          :toShow="confirmationDialog.toShow"
          :title="confirmationDialog.title"
          :content="confirmationDialog.content"
          :onConfirm="confirmationDialog.callback"
          :valueCallback="confirmationDialog.valueCallback"
          :viewOnly="viewOnly"
        />
      </section>

      <section class="jd-compact-route-strip">
        <div class="jd-compact-route-strip__point">
          <span>Source</span>
          <strong>{{ clientSourceSummary || "NA" }}</strong>
        </div>
        <div class="jd-compact-stepper">
          <div
            v-for="step in clientTrackingSteps"
            :key="step.label"
            class="jd-compact-step"
            :class="{
              'jd-compact-step--complete': step.complete,
              'jd-compact-step--active': step.active,
            }"
          >
            <span></span>
            <v-icon v-if="step.active" small>
              {{ getTrackingIcon(jobTypeArray[e1 - 1]) }}
            </v-icon>
            <small>{{ step.label }}</small>
          </div>
        </div>
        <div class="jd-compact-route-strip__point">
          <span>Destination</span>
          <strong>{{ clientDestinationSummary || "NA" }}</strong>
        </div>
      </section>

      <section class="jd-section jd-section--lanes">
        <v-stepper v-model="e1" class="jd-stepper">
          <v-stepper-header class="SubTypeTab jd-lane-tabs">
            <template v-for="n in steps" class="bg-clr-tab">
              <v-stepper-step
                :complete="e1 == n"
                :key="`${n}-step`"
                :step="n"
                :editable="true"
                color="orange darken-1"
                :complete-icon="getJobTypeIcon(jobTypeArray[n - 1])"
                :edit-icon="getJobTypeIcon(jobTypeArray[n - 1])"
                class="bg-clr-tab"
              >
                <v-icon small class="jd-lane-tab-icon">
                  {{ getJobTypeIcon(jobTypeArray[n - 1]) }}
                </v-icon>
                <span>{{ getJobType(jobTypeArray[n - 1]) }} {{ n }}</span>
              </v-stepper-step>
            </template>
          </v-stepper-header>

          <v-stepper-items>
            <v-stepper-content
              v-for="n in steps"
              :key="`${n}-content`"
              :step="n"
            >
              <div class="jd-lane-workbench">
                <div class="jd-lane-workbench__main">
                  <JobsProfileSubJobDetail
                    :detail="profile"
                    :isAssignDriver="isAssignDriver[n - 1]"
                    :isAssignCustomerRecipt="isAssignCustomerRecipt[n - 1]"
                    :totalAdditionalAmountTab="totalAdditionalAmountTab[n - 1]"
                    :assignments="assignments[n - 1]"
                    :userDetail="customerDetails"
                    :jobId="profile.jobId"
                    v-on:updateJobs="updateJobs"
                    :job="jobID"
                    :loader="loading"
                    :tabOrder="n"
                    :jobType="jobTypeArray[n - 1]"
                    :viewOnly="viewOnly"
                  />
                </div>

                <aside class="jd-lane-workbench__quote">
                  <SendQuote
                    v-permissions="'send-quotes-jobs'"
                    :detail="profile"
                    :userId="profile.customerId"
                    :jobId="profile.jobId"
                    :workingcountry="profile.workingCountry"
                    :deliveryProof="deliveryProof"
                    :assignment="assignments[n - 1]"
                    :costJobtype="costJobtype[n - 1]"
                    :currency="profile.customerCurrency"
                    v-on:updateJobs="updateJobs"
                    :pricing="prices[n - 1]"
                    :subJobDetail="subJobArray[n - 1]"
                    :viewOnly="viewOnly"
                  />
                </aside>
              </div>

              <div class="jd-assignments-panel">
                <JobsProfileAssignmentList
                  :assignments="assignments[n - 1]"
                  :userDetail="customerDetails"
                  :driverDoc="driverDoc[n - 1]"
                  :milestoneItemsList="milestoneItemArray"
                  :jobId="profile.jobId"
                  v-on:updateJobs="updateJobs"
                  :job="jobID"
                  :journeyDis="journeyDis"
                  :journeyDays="journeyDays"
                  :loader="loading"
                  :jobData="jobData"
                  :milestoneList="milestoneItemArray"
                  :profile="profile"
                  :workingcountry="profile.workingCountry"
                  :tabOrder="n"
                  :jobType="jobTypeArray[n - 1]"
                  :viewOnly="viewOnly"
                />
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </section>
    </div>

    <div v-else-if="jobDetailsViewMode === 'clientsView'" class="jd-client-file">
      <section class="jd-client-hero">
        <div>
          <span>Job {{ profile.jobId || jobID }}</span>
          <h1>{{ clientHeroTitle }}</h1>
          <p v-if="clientEtaDisplay">ETA {{ clientEtaDisplay }}</p>
        </div>
        <a
          v-if="clientDocumentUrl"
          class="jd-client-download"
          :href="clientDocumentUrl"
          target="_blank"
          rel="noopener"
        >
          <v-icon small>cloud_download</v-icon>
          Download Documents
        </a>
        <button v-else type="button" class="jd-client-download" disabled>
          <v-icon small>cloud_download</v-icon>
          Documents Pending
        </button>
      </section>

      <section class="jd-client-track">
        <div
          v-for="step in clientTrackingSteps"
          :key="step.label"
          class="jd-client-track__step"
          :class="{
            'jd-client-track__step--complete': step.complete,
            'jd-client-track__step--active': step.active,
          }"
        >
          <span>
            <v-icon v-if="step.active" small>
              {{ getTrackingIcon(jobTypeArray[e1 - 1]) }}
            </v-icon>
          </span>
          <strong>{{ step.label }}</strong>
        </div>
      </section>

      <section class="jd-client-grid">
        <article class="jd-client-overview jd-client-overview--route">
          <div class="jd-client-section-title">
            <span>Route Specs</span>
            <strong>{{ customerDisplayName }}</strong>
          </div>

          <div class="jd-client-kv-grid">
            <div>
              <span>Origin</span>
              <strong>{{ clientSourceSummary || "NA" }}</strong>
            </div>
            <div>
              <span>Destination</span>
              <strong>{{ clientDestinationSummary || "NA" }}</strong>
            </div>
            <div v-if="clientPickupDisplay">
              <span>Pickup Date</span>
              <strong>{{ clientPickupDisplay }}</strong>
            </div>
          </div>
        </article>

        <article class="jd-client-overview jd-client-overview--cargo">
          <div class="jd-client-section-title">
            <span>Cargo / Weights</span>
            <strong>{{ clientCargoTitle || "Manifest" }}</strong>
          </div>
          <div class="jd-client-subsection" v-if="clientManifestRows.length">
            <span>Itemized Cargo Manifest</span>
            <div class="jd-client-manifest">
              <div v-for="row in clientManifestRows" :key="row.label">
                <small>{{ row.label }}</small>
                <strong>{{ row.value }}</strong>
              </div>
            </div>
          </div>

          <div class="jd-client-subsection">
            <span>Live Driver Updates</span>
            <div v-if="clientDriverUpdates.length" class="jd-client-driver-list">
              <div v-for="driver in clientDriverUpdates" :key="driver.key">
                <strong>{{ driver.name }}</strong>
                <small>{{ driver.status }}</small>
                <small v-if="driver.truck">{{ driver.truck }}</small>
              </div>
            </div>
            <div v-else class="jd-client-empty">NA</div>
          </div>
        </article>

        <article class="jd-client-finance">
          <div class="jd-client-section-title">
            <span>Billing / Payer Details</span>
            <strong>{{ clientTotalAmountDue }}</strong>
          </div>
          <div
            class="jd-client-payment-badge"
            :class="'jd-client-payment-badge--' + jobStatusClass"
          >
            {{ clientPaymentStatusLabel }}
          </div>

          <div class="jd-client-finance-rows">
            <div v-for="row in clientFinanceRows" :key="row.label">
              <span>{{ row.label }}</span>
              <strong>{{ row.value }}</strong>
            </div>
          </div>
        </article>
      </section>

      <section class="jd-bento-assignments">
        <div class="jd-client-section-title">
          <span>Assignments & Logs</span>
          <strong>{{ totalAssignmentCount }} assignment records</strong>
        </div>
        <v-stepper v-model="e1" class="jd-stepper jd-stepper--bento">
          <v-stepper-header class="SubTypeTab jd-lane-tabs">
            <template v-for="n in steps" class="bg-clr-tab">
              <v-stepper-step
                :complete="e1 == n"
                :key="`${n}-bento-step`"
                :step="n"
                :editable="true"
                color="orange darken-1"
                :complete-icon="getJobTypeIcon(jobTypeArray[n - 1])"
                :edit-icon="getJobTypeIcon(jobTypeArray[n - 1])"
                class="bg-clr-tab"
              >
                <v-icon small class="jd-lane-tab-icon">
                  {{ getJobTypeIcon(jobTypeArray[n - 1]) }}
                </v-icon>
                <span>{{ getJobType(jobTypeArray[n - 1]) }} {{ n }}</span>
              </v-stepper-step>
            </template>
          </v-stepper-header>

          <v-stepper-items>
            <v-stepper-content
              v-for="n in steps"
              :key="`${n}-bento-content`"
              :step="n"
            >
              <JobsProfileAssignmentList
                :assignments="assignments[n - 1]"
                :userDetail="customerDetails"
                :driverDoc="driverDoc[n - 1]"
                :milestoneItemsList="milestoneItemArray"
                :jobId="profile.jobId"
                v-on:updateJobs="updateJobs"
                :job="jobID"
                :journeyDis="journeyDis"
                :journeyDays="journeyDays"
                :loader="loading"
                :jobData="jobData"
                :milestoneList="milestoneItemArray"
                :profile="profile"
                :workingcountry="profile.workingCountry"
                :tabOrder="n"
                :jobType="jobTypeArray[n - 1]"
                :viewOnly="viewOnly"
              />
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </section>
    </div>

    <div v-else class="jd-master-detail">
      <aside class="jd-master-pane jd-master-pane--timeline">
        <div class="jd-pane-title">
          <span>Timeline</span>
          <strong>{{ jobStatusLabel }}</strong>
        </div>
        <div class="jd-vertical-route">
          <div
            v-for="step in clientTrackingSteps"
            :key="step.label"
            class="jd-vertical-route__step"
            :class="{
              'jd-vertical-route__step--complete': step.complete,
              'jd-vertical-route__step--active': step.active,
            }"
          >
            <span>
              <v-icon v-if="step.active" small>
                {{ getTrackingIcon(jobTypeArray[e1 - 1]) }}
              </v-icon>
            </span>
            <div>
              <strong>{{ step.label }}</strong>
              <small v-if="step.label === 'Order Placed'">
                {{ clientSourceSummary || "NA" }}
              </small>
              <small v-else-if="step.label === 'Delivered'">
                {{ clientDestinationSummary || "NA" }}
              </small>
              <small v-else>{{ jobDateDisplay || clientEtaDisplay || "NA" }}</small>
            </div>
          </div>
        </div>
      </aside>

      <main class="jd-master-pane jd-master-pane--core">
        <div class="jd-pane-title">
          <span>Core File</span>
          <strong>Job {{ profile.jobId || jobID }}</strong>
        </div>
        <div class="jd-master-table">
          <div v-for="row in clientManifestRows" :key="row.label">
            <span>{{ row.label }}</span>
            <strong>{{ row.value }}</strong>
          </div>
          <div v-for="row in clientFinanceRows" :key="`finance-${row.label}`">
            <span>{{ row.label }}</span>
            <strong>{{ row.value }}</strong>
          </div>
        </div>

        <JobsProfileDetail
          :detail="profile"
          :workingcountry="profile.workingCountry"
          :userDetail="customerDetails"
          v-on:updateJobs="updateJobs"
          :viewOnly="viewOnly"
        />
      </main>

      <aside class="jd-master-pane jd-master-pane--ops">
        <div class="jd-pane-title">
          <span>Operations</span>
          <strong>{{ totalAssignmentCount }} Assignments</strong>
        </div>
        <div class="jd-stakeholder-list">
          <div>
            <span>Customer</span>
            <strong>{{ customerDisplayName || "NA" }}</strong>
          </div>
          <div>
            <span>Primary Driver</span>
            <strong>{{ clientDriverUpdates[0] ? clientDriverUpdates[0].name : "NA" }}</strong>
          </div>
          <div>
            <span>Vehicle</span>
            <strong>{{ clientDriverUpdates[0] ? clientDriverUpdates[0].truck || "NA" : "NA" }}</strong>
          </div>
        </div>

        <v-stepper v-model="e1" class="jd-stepper jd-stepper--master">
          <v-stepper-header class="SubTypeTab jd-lane-tabs">
            <template v-for="n in steps" class="bg-clr-tab">
              <v-stepper-step
                :complete="e1 == n"
                :key="`${n}-master-step`"
                :step="n"
                :editable="true"
                color="orange darken-1"
                :complete-icon="getJobTypeIcon(jobTypeArray[n - 1])"
                :edit-icon="getJobTypeIcon(jobTypeArray[n - 1])"
                class="bg-clr-tab"
              >
                <v-icon small class="jd-lane-tab-icon">
                  {{ getJobTypeIcon(jobTypeArray[n - 1]) }}
                </v-icon>
                <span>{{ getJobType(jobTypeArray[n - 1]) }} {{ n }}</span>
              </v-stepper-step>
            </template>
          </v-stepper-header>

          <v-stepper-items>
            <v-stepper-content
              v-for="n in steps"
              :key="`${n}-master-content`"
              :step="n"
            >
              <JobsProfileAssignmentList
                :assignments="assignments[n - 1]"
                :userDetail="customerDetails"
                :driverDoc="driverDoc[n - 1]"
                :milestoneItemsList="milestoneItemArray"
                :jobId="profile.jobId"
                v-on:updateJobs="updateJobs"
                :job="jobID"
                :journeyDis="journeyDis"
                :journeyDays="journeyDays"
                :loader="loading"
                :jobData="jobData"
                :milestoneList="milestoneItemArray"
                :profile="profile"
                :workingcountry="profile.workingCountry"
                :tabOrder="n"
                :jobType="jobTypeArray[n - 1]"
                :viewOnly="viewOnly"
              />
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </aside>
    </div>
  </v-container>
</template>

<script>
import moment from "moment";
import SendQuote from "./SendQuote";
import { EventBus } from "../../../event-bus.js";
import ConfirmationDialog from "@/components/Common/ConfirmationDialog";
import JobsProfileAssignmentList from "./JobsProfileAssignmentList";
import JobsProfileDetail from "./JobsProfileDetail.vue";
import JobsProfileSubJobDetail from "./JobsProfileSubJobDetail.vue";
import {
  TruckTypes,
  PackingTypes,
  NatureOfCargos,
} from "../../../constants/constants";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.min.css";
import { StorageKeys } from "../../../constants/constants";
import { milestoneList, generateWorkOrder } from "../../../constants/api-urls";

export default {
  created() {
    this.$eventBus.$on("job-details-sales-invoice", (data) => {
      console.log(data);
      if (this.$route.params.id == data.socketSalesInvoiceData.jobId) {
        this.updateProfileFromSocket(data.socketSalesInvoiceData);
      }
    });
    this.$eventBus.$on("job-details-sales-order", (data) => {
      console.log(data);
      if (this.$route.params.id == data.socketSalesOrderData.jobId) {
        this.updateProfileFromSocket(data.socketSalesOrderData);
      }
    });

    if (localStorage.getItem("workingCountries")) {
      this.workingCountries = JSON.parse(
        localStorage.getItem("workingCountries")
      );
      let workingCountry = [];
      this.workingCountries.forEach((key, index) => {
        if (key.isSelected) {
          workingCountry.push(key.value);
        }
      });
      this.workingCountry = JSON.stringify(workingCountry);
      this.getMilestones();
    }

    EventBus.$on("countryChanged", (val) => {
      if (
        this.$route.name == "jobs-profile" ||
        this.$route.name == "jobs-profile-view"
      ) {
        this.$router.go(-1);
      }
    });
    this.$eventBus.$on("searchText", function (e) {});
    this.$eventBus.$on("refresh-job", () => {
      console.log("refresh-jobsss");
      const { id } = this.$route.params;
      this.jobID = this.$route.params.id;
      this.fetchDetailFromParam(id);
    });
    this.$eventBus.$on("refresh-milstoneChange", () => {
      console.log("refresh-jobsss");
      const { id } = this.$route.params;
      this.jobID = this.$route.params.id;
      this.fetchDetailFromParam(id);
    });

    this.$eventBus.$on("off-load", () => {
      this.loading = false;
    });
    this.$eventBus.$on("on-load", () => {
      this.loading = true;
    });
  },
  beforeDestroy() {
    this.$eventBus.$off("refresh-job");
  },
  mounted() {
    const { id } = this.$route.params;
    this.jobID = this.$route.params.id;
    const savedJobDetailsViewMode = localStorage.getItem("jobDetailsViewMode");
    if (savedJobDetailsViewMode === "modern") {
      this.jobDetailsViewMode = savedJobDetailsViewMode;
    } else {
      this.jobDetailsViewMode = "modern";
      localStorage.setItem("jobDetailsViewMode", "modern");
    }

    if (this.$route.query && this.$route.query.workingCountry) {
      if (localStorage.getItem("workingCountries")) {
        this.workingCountries = JSON.parse(
          localStorage.getItem("workingCountries")
        );
        this.workingCountries.forEach((key, index) => {
          if (key.isSelected) {
            this.workingCountry = key.value;
          }
        });
      }
      let key = this.$route.query.workingCountry;
      EventBus.$emit("setCountriesfromUrl", key);
      this.workingCountries.forEach((element, index) => {
        if (element.value === key) {
          element.isSelected = true;
        } else element.isSelected = false;
      });
      localStorage.setItem(
        "workingCountries",
        JSON.stringify(this.workingCountries)
      );
    }
    this.viewOnly = this.$route.params.viewOnly
      ? this.$route.params.viewOnly
      : "";

    this.truckTypes = TruckTypes;
    this.natureOfCargos = NatureOfCargos;
    this.packingTypes = PackingTypes;
    this.jobTypeArray = [];
    this.fetchDetailFromParam(id);
    this.$eventBus.$on("close-confirmation-dialog", () => {
      this.confirmationDialog.toShow = false;
    });

    this.$eventBus.$on("driver-assignment", (data) => {
      const { assignmentId, driverId, driverName } = data;
      const assignment = this.assignments.find(
        (a) => a.assignmentId === assignmentId
      );
      if (assignment) {
        assignment.driverId = driverId;
        assignment.driverName = driverName;
      }
    });
  },
  components: {
    JobsProfileAssignmentList,
    ConfirmationDialog,
    JobsProfileDetail,
    Loading,
    SendQuote,
    JobsProfileSubJobDetail,
  },
  data: () => ({
    milestoneItems: [],
    workingCountry: "",
    country: "",
    prices: [],
    milestoneItems: [],
    journeyDays: "",
    journeyDis: "",
    jobDetails: [],
    e1: 1,
    steps: 1,
    jobDetailsViewMode: "modern",
    jobDetailsViewModes: [],
    costJobtype: [],
    editable: false,
    showQuote: false,
    loading: false,
    deliveryProof: [],
    driverAssignModal: false,
    driverId: null,
    newDriverName: null,
    customerDetails: null,
    assignments: [],
    isAssignDriver: [],
    isAssignCustomerRecipt: [],
    totalAdditionalAmountTab: [],
    driverDoc: [],
    confirmationDialog: {
      toShow: false,
      title: null,
      content: null,
      callback: null,
      valueCallback: null,
    },
    readMode: true,
    menu: false,
    error: null,
    startDateStr: null,
    profile: {
      jobId: null,
      from: null,
      to: null,
      workingCountry: "",
      amount: "",
      notes: null,
      driverName: null,
      waybillNumber: "NA",
      paymentAmount: "",
      status: 0,
      customer: null,
      customerCurrency: null,
      jobStatus: null,
      startDate: null,
      deliveryDate: null,
      otherDetails: null,
      packingType: null,
      orderType: 1,
      quantity: null,
      sourceCity: null,
      chargesType: null,
      sourceCountry: null,
      destinationCity: null,
      isTransporterJob: false,
      isDriverJob: false,
      destinationCountry: null,
      sourceLocation: null,
      destinationLocation: null,
      invoiceCity: null,
      invoiceCountry: null,
      sourceLatLong: [],
      destinationLatLong: [],
      natureOfCargo: null,
      truckType: null,
      invoicingCompany: "",
      invoicingCity: "",
      numberOfAssignmentsRequired: null,
    },
    jobData: "",
    truckTypes: [],
    natureOfCargos: [],
    packingTypes: [],
    jobID: null,
    viewOnly: "",
    jobTypeArray: [],
    milestoneItemArray: [],
    isDriverJobArray: [],
    isTransporterJobArray: [],
    amountForTransporterArray: [],
    subJobArray: [],
  }),
  watch: {
    jobID() {},
    menu(val) {
      val && this.$nextTick(() => (this.$refs.picker.activePicker = "YEAR"));
    },
    steps(val) {
      if (this.e1 > val) {
        this.e1 = val;
      }
    },
  },
  computed: {
    customerDisplayName() {
      if (this.profile.companyName) {
        return this.profile.companyName;
      }
      if (this.customerDetails && this.customerDetails.companyName) {
        return this.customerDetails.companyName;
      }
      if (this.profile.customer) {
        return this.profile.customer;
      }
      if (this.profile.customerEmail) {
        return this.profile.customerEmail;
      }
      return this.profile.customerId || "";
    },
    jobDateDisplay() {
      return this.profile.startDate ? this.getTime(this.profile.startDate) : "";
    },
    jobStatusClass() {
      switch (this.profile.jobStatus) {
        case 1:
        case 3:
        case 4:
          return "open";
        case 5:
        case 6:
        case 7:
          return "moving";
        case 8:
        case 10:
          return "pending";
        case 2:
        case 9:
          return "done";
        case -1:
          return "cancelled";
        default:
          return "neutral";
      }
    },
    jobStatusLabel() {
      return this.getStatus(this.profile.jobStatus);
    },
    allAssignments() {
      if (!this.assignments || !this.assignments.length) {
        return [];
      }
      return this.assignments.reduce((list, group) => {
        return group && group.length ? list.concat(group) : list;
      }, []);
    },
    totalAssignmentCount() {
      return this.allAssignments.length;
    },
    clientPrimaryAssignment() {
      return this.allAssignments.length ? this.allAssignments[0] : {};
    },
    clientSourceSummary() {
      return this.formatClientLocation(
        this.profile.sourceCity || this.clientPrimaryAssignment.sourceCity,
        this.profile.sourceCountry || this.clientPrimaryAssignment.sourceCountry,
        this.profile.sourceLocation || this.clientPrimaryAssignment.sourceLocation
      );
    },
    clientDestinationSummary() {
      return this.formatClientLocation(
        this.profile.destinationCity ||
          this.clientPrimaryAssignment.destinationCity,
        this.profile.destinationCountry ||
          this.clientPrimaryAssignment.destinationCountry,
        this.profile.destinationLocation ||
          this.clientPrimaryAssignment.destinationLocation
      );
    },
    clientHeroTitle() {
      return this.clientDestinationSummary
        ? `${this.jobStatusLabel} - ${this.clientDestinationSummary}`
        : this.jobStatusLabel;
    },
    clientEtaDisplay() {
      const eta =
        this.profile.deliveryDate ||
        this.clientPrimaryAssignment.deliveryDate ||
        this.clientPrimaryAssignment.offloadedDate;
      return eta ? this.getTime(eta) : "";
    },
    clientPickupDisplay() {
      const pickup =
        this.profile.startDate || this.clientPrimaryAssignment.pickupDate;
      return pickup ? this.getTime(pickup) : "";
    },
    clientCargoTitle() {
      if (this.profile.commodity) {
        return this.profile.commodity;
      }
      if (this.profile.natureOfCargo && this.profile.natureOfCargo.name) {
        return this.profile.natureOfCargo.name;
      }
      if (this.clientPrimaryAssignment.commodity) {
        return this.clientPrimaryAssignment.commodity;
      }
      return "";
    },
    clientManifestRows() {
      return [
        {
          label: "Cargo",
          value: this.clientCargoTitle,
        },
        {
          label: "Quantity",
          value: this.profile.quantity || this.clientPrimaryAssignment.quantity,
        },
        {
          label: "Packing Type",
          value: this.profile.packingType
            ? this.getText(this.packingTypes, this.profile.packingType)
            : "",
        },
        {
          label: "Required Units",
          value: this.profile.numberOfAssignmentsRequired,
        },
      ].filter((row) => row.value);
    },
    clientDriverUpdates() {
      return this.allAssignments
        .filter((assignment) => assignment.driverFirstName || assignment.driverData)
        .map((assignment) => {
          const driverName = assignment.driverFirstName
            ? `${assignment.driverFirstName || ""} ${
                assignment.driverLastName || ""
              }`.trim()
            : assignment.driverData && assignment.driverData.name;
          const truckPrefix =
            assignment.truckPrefix ||
            (assignment.driverData && assignment.driverData.truckPrefix) ||
            "";
          const truckNumber =
            assignment.truckNumber ||
            (assignment.driverData && assignment.driverData.truckNumber) ||
            "";
          return {
            key: assignment.assignmentId || assignment._id,
            name: driverName || "NA",
            status: this.getClientAssignmentStatus(assignment.assignmentStatus),
            truck: [truckPrefix, truckNumber].filter(Boolean).join("-"),
          };
        });
    },
    clientDocumentUrl() {
      return this.profile.invoicePdfUrl || this.profile.salesInvoicePdfUrl || "";
    },
    clientTotalAmountDue() {
      return this.profile.jobPrice
        ? `${this.$formatPrice(this.profile.jobPrice)} ${
            this.profile.customerCurrency || ""
          }`
        : "NA";
    },
    clientPaymentStatusLabel() {
      return this.profile.jobStatus === 8 || this.profile.jobStatus === 9
        ? this.jobStatusLabel
        : this.jobStatusLabel;
    },
    clientPayerCategory() {
      return this.profile.paymentEntity || this.profile.charge || "NA";
    },
    clientBillingEntity() {
      return (
        this.profile.companyName ||
        this.profile.customer ||
        this.profile.customerEmail ||
        "NA"
      );
    },
    clientFinanceRows() {
      return [
        {
          label: "Total Amount Due",
          value: this.clientTotalAmountDue,
        },
        {
          label: "Payment Status",
          value: this.clientPaymentStatusLabel,
        },
        {
          label: "Billing Entity",
          value: this.clientBillingEntity,
        },
        {
          label: "Payer Category",
          value: this.clientPayerCategory,
        },
        {
          label: "Invoice Number",
          value: this.profile.invoiceNumber,
        },
      ].filter((row) => row.value);
    },
    clientTrackingSteps() {
      const status = this.profile.jobStatus;
      let currentIndex = 0;
      if (status === 5) {
        currentIndex = 1;
      } else if (status === 6) {
        currentIndex = 2;
      } else if (
        status === 2 ||
        status === 7 ||
        status === 8 ||
        status === 9 ||
        status === 11
      ) {
        currentIndex = 3;
      }
      return ["Order Placed", "Picked Up", "In Transit", "Delivered"].map(
        (label, index) => ({
          label,
          active: index === currentIndex,
          complete: index < currentIndex,
        })
      );
    },
    natureOfCargoStr() {
      return this.natureOfCargo
        ? this.getText(this.natureOfCargos, this.natureOfCargo)
        : "";
    },
    truckTypeStr() {
      return this.truckType
        ? this.getText(this.truckTypes, this.truckType)
        : "";
    },
    packingTypeStr() {
      return this.packingType
        ? this.getText(this.packingTypes, this.packingType)
        : "";
    },
    jobStartDateStr: {
      get() {
        return this.profile && this.profile.startDate
          ? this.getTime(this.profile.startDate)
          : null;
      },
      set(val) {
        const epoch = moment(val, "DD/MM/YYYY").unix();
        this.profile.startDate = epoch;
      },
    },
  },
  methods: {
    updateProfileFromSocket(socketProfile = {}) {
      const currentJobStatus = this.profile.jobStatus;
      const updatedProfile = {
        ...this.profile,
        ...socketProfile,
      };

      if (
        socketProfile.jobStatus === undefined ||
        socketProfile.jobStatus === null ||
        socketProfile.jobStatus === ""
      ) {
        updatedProfile.jobStatus = currentJobStatus;
      } else {
        updatedProfile.jobStatus = Number(socketProfile.jobStatus);
      }

      this.profile = updatedProfile;
    },
    formatClientLocation(city, country, location) {
      const cityCountry = [city, country].filter(Boolean).join(", ");
      return cityCountry || location || "";
    },
    getClientAssignmentStatus(status) {
      switch (status) {
        case 1:
          return "Awaiting Driver";
        case 2:
          return "Driver Assigned";
        case 3:
          return "Cancelled";
        case 4:
          return "Rejected";
        case 5:
          return "Picked Up";
        case 6:
          return "In Transit";
        case 7:
          return "Delivered";
        case 8:
          return "Payment Pending";
        case -1:
          return "Cancelled";
        default:
          return "NA";
      }
    },
    setJobDetailsViewMode(view) {
      if (view !== "modern") {
        return;
      }
      this.jobDetailsViewMode = view;
      localStorage.setItem("jobDetailsViewMode", view);
    },
    roundOff(value, decimals) {
      return Number(Math.round(value + "e" + decimals) + "e-" + decimals);
    },
    retry(jobId) {
      let url = generateWorkOrder;
      delete this.axios.defaults.headers.common["token"];
      let token = localStorage.getItem(StorageKeys.SessionKey);
      let config = {
        headers: {
          authorization: `bearer ${token}`,
        },
      };
      let body = { jobId: jobId.toString };

      this.axios.post(this.constants.apiUrl + url, body, config).then(
        (response) => {},
        (error) => {
          this.error = "Failed to Generate Work Order";
        }
      );
    },
    checkAllassignments(assignments) {
      let complete = true;
      assignments.forEach((element) => {
        if (element.assignmentStatus == 1 || element.assignmentStatus == 2) {
          complete = false;
        }
      });
      return !complete;
    },
    updateJobs() {
      const { id } = this.$route.params;
      this.fetchDetailFromParam(id);
    },
    goBackToJobs() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push("/jobs");
      }
    },

    paymentAmountCb(amount) {
      this.profile.paymentAmount = amount;
    },
    save(date) {
      this.profile.startDate = moment(date, "YYYY-MM-DD").unix();
      this.$refs.menu.save(date);
    },
    getNow() {
      return moment().unix();
    },
    cancelJob() {
      this.profile.jobStatus = -1;
    },
    finishJob() {
      this.profile.jobStatus = 2;
    },
    makePayment() {
      this.profile.jobStatus = 3;
    },

    showConfirmation(
      job = "Operation",
      confirmCallback,
      valueCallback,
      description = ""
    ) {
      this.loading = false;
      this.confirmationDialog = {
        toShow: true,
        title: `${job} Confirmation`,
        content: !description ? `Are you sure to ${job}?` : description,
        callback: confirmCallback,
        valueCallback,
      };
    },
    defaultDialogCloseCallback() {
      this.confirmationDialog = {
        toShow: false,
        title: null,
        content: null,
      };
    },
    getTime(time) {
      if (!time) {
        return;
      }
      const unixMoment = moment.unix(time);
      const formatStr = unixMoment.format("DD/MM/YYYY");
      return formatStr;
    },
    getJobType(type) {
      switch (type) {
        case "1":
          return "Land";
        case "2":
          return "Air";
        case "3":
          return "Sea";
        default:
          return "Land";
      }
    },
    getJobTypeIcon(type) {
      switch (type) {
        case "2":
          return "flight_takeoff";
        case "3":
          return "directions_boat";
        default:
          return "local_shipping";
      }
    },
    getTrackingIcon(type) {
      switch (type) {
        case "2":
          return "flight_takeoff";
        case "3":
          return "directions_boat";
        default:
          return "local_shipping";
      }
    },
    getStatus(status) {
      switch (Number(status)) {
        case 1:
          return "Active";
        case 2:
          return "Completed";
        case 3:
          return "Customer Rate Accepted";
        case 4:
          return "Finding Drivers";
        case 5:
          return "Driver Assigned";
        case 6:
          return "In-Transit";
        case 7:
          return "Shipment-OffLoaded";
        case 8:
          return "Payment Pending";
        case 9:
          return "Payment Done";
        case 10:
          return "Low Credit Limit";
        case 11:
          return "Document Received";
        case 12:
          return "Driver Partially Assigned";
        case -1:
          return "Job Cancelled";
        default:
          return "Unknown";
      }
    },
    closeModal() {
      this.$eventBus.$emit("close-job-profile");
    },
    updateJob() {
      this.readMode = true;
    },
    getText(arr = [], val) {
      let finalEl = arr.find((el) => el.value === val);
      return finalEl ? finalEl.value : "NA";
    },
    getMilestones() {
      let url = milestoneList;
      delete this.axios.defaults.headers.common["token"];
      let token = localStorage.getItem(StorageKeys.SessionKey);
      let config = {
        headers: {
          authorization: `bearer ${token}`,
        },
      };
      let body = {
        workingCountry: this.workingCountry,
        assignDriver: "true",
      };

      this.axios.post(this.constants.apiUrl + url, body, config).then(
        (response) => {
          localStorage.removeItem("mileStone");
          this.milestoneItemArray = response.data.data;
          if (response.data.data) {
            localStorage.setItem(
              "mileStone",
              JSON.stringify(response.data.data)
            );
          }
        },
        (error) => {
          this.error = "Failed to Fetch Milestone Status";
        }
      );
    },
    fetchDetailFromParam(id) {
      this.loading = true;
      if (id === "new") {
        return; // default profile
      }
      delete this.axios.defaults.headers.common["token"];
      let token = localStorage.getItem(StorageKeys.SessionKey);
      let config = {
        headers: {
          authorization: `bearer ${token}`,
        },
      };
      let body = {
        jobId: id,
      };
      let { apiUrl } = this.constants;
      this.axios.post(`${apiUrl}/dmsAdmin/jobDetails`, body, config).then(
        (response) => {
          const data = response.data.data;
          const status = response.status;
          const { jobInfo = null, assignmentList = [] } = data;
          if (status === 200 && jobInfo) {
            this.profile = Object.assign({}, jobInfo);
            if (
              jobInfo.jobStatus !== undefined &&
              jobInfo.jobStatus !== null &&
              jobInfo.jobStatus !== ""
            ) {
              this.profile.jobStatus = Number(jobInfo.jobStatus);
            }
            var size = assignmentList.length;
            this.steps = size;
            this.assignments = [];
            this.costJobtype = [];
            this.jobTypeArray = [];
            this.isTransporterJobArray = [];
            this.isDriverJobArray = [];
            this.prices = [];
            this.amountForTransporterArray = [];
            this.subJobArray = [];
            for (var i = 0; i < size; i++) {
              this.assignments[i] = assignmentList[i].assignmentListing;
              this.isAssignDriver[i] = assignmentList[i].isAssignDriver;
              this.isAssignCustomerRecipt[i] =
                assignmentList[i].isCustomerReceiptInitiated;
              this.totalAdditionalAmountTab[i] =
                assignmentList[i].totalAdditionalAmount;
              this.driverDoc[i] = assignmentList[i].driverDoc;
              this.costJobtype[i] = assignmentList[i].subJobPrice;
              this.jobTypeArray[i] = assignmentList[i].jobType || "1";
              this.isDriverJobArray[i] = assignmentList[i].isDriverJob
                ? true
                : false;
              this.isTransporterJobArray[i] = assignmentList[i].isTransporterJob
                ? true
                : false;
              this.subJobArray[i] = assignmentList[i];

              var prices = {
                driverCost: 0,
                customerCost: null,
                trukkinCost: null,
                AdditionalCost: null,
                dutyCharges: null,
              };
              assignmentList[i].assignmentListing.forEach((e) => {
                e.isDriverAssignPopUp = false;
                e.isDriverFormPopUp = false;
                e.isDriverpdfPopUp = false;
                e.isDriverUnAssignPopUp = false;
                e.isTransporterUnAssignPopUp = false;
                e.isDriverAssetPopUp = false;
                if (e.paymentAmount && e.assignmentStatus != -1) {
                  const driverCost = parseFloat(prices.driverCost) || 0;
                  const paymentAmount = parseFloat(e.paymentAmount) || 0;
                  prices.driverCost = Number(
                    (driverCost + paymentAmount).toFixed(2)
                  );
                }
              });
              prices.customerCost = assignmentList[i].subJobPrice;
              prices.dutyCharges = assignmentList[i].totalDutyValue;
              prices.trukkinCost = prices.customerCost - prices.driverCost;
              if (assignmentList[i].additionalCharges) {
                prices.additionalCost =
                  assignmentList[i].additionalCharges.toFixed(2);
                prices.trukkinCost = prices.trukkinCost;
              }
              this.prices[i] = prices;
            }
            this.deliveryProof = data.jobInfo.deliveryProof;

            this.customerDetails = data.userData;
            this.jobData = data.jobData;
            this.journeyDays = data.journeyDays;
            this.journeyDis = data.journeyDis;
            this.showQuote = this.checkAllassignments(this.assignments);
          } else {
            this.error = "Something went wrong. Please try again later!";
          }
          this.loading = false;
        },
        (error) => {
          this.error = "Something went wrong. Please try again later!";
          this.loading = false;
        }
      );
    },
  },
};
</script>

<style lang="scss">
.job-dossier {
  --jd-bg: #f4f6fa;
  --jd-panel: #ffffff;
  --jd-border: #e1e7f0;
  --jd-border-strong: #d2dae7;
  --jd-ink: #172033;
  --jd-muted: #667085;
  --jd-soft: #fff7ed;
  --jd-orange: #f58220;
  --jd-orange-dark: #a85510;
  --jd-blue: #3157d5;
  --jd-green: #11875d;
  --jd-red: #b42318;
  background:
    linear-gradient(180deg, #f9fafc 0%, var(--jd-bg) 280px),
    var(--jd-bg);
  color: var(--jd-ink);
  min-height: calc(100vh - 54px);
  padding: 22px 28px 38px !important;
}

.jd-command,
.jd-intelligence,
.jd-client-overview,
.jd-progress,
.jd-section {
  margin: 0 auto 16px;
  max-width: none;
  width: 100%;
}

.jd-command {
  background: var(--jd-panel);
  border: 1px solid var(--jd-border);
  border-radius: 12px;
  box-shadow: 0 20px 45px rgba(23, 32, 51, 0.08);
  overflow: hidden;
}

.jd-command__nav {
  align-items: center;
  background: #fbfcfe;
  border-bottom: 1px solid var(--jd-border);
  display: flex;
  gap: 10px;
  min-height: 52px;
  padding: 10px 18px;
}

.jd-command__nav span,
.jd-kicker span,
.jd-section-label,
.jd-intel-card span,
.jd-section-header span,
.jd-mini-heading span,
.jd-progress-step strong {
  color: var(--jd-muted);
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.jd-back {
  align-items: center;
  background: #ffffff;
  border: 1px solid var(--jd-border);
  border-radius: 9px;
  color: var(--jd-orange-dark);
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 12px;
  font-weight: 900;
  gap: 7px;
  min-height: 34px;
  padding: 7px 11px;
}

.jd-view-switch {
  align-items: center;
  background: #eef2f7;
  border: 1px solid var(--jd-border);
  border-radius: 10px;
  display: inline-flex;
  gap: 3px;
  margin-left: auto;
  padding: 3px;
}

.jd-view-switch button {
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: var(--jd-muted);
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 900;
  min-height: 32px;
  padding: 7px 13px;
}

.jd-view-switch button.active {
  background: #ffffff;
  box-shadow: 0 3px 10px rgba(23, 32, 51, 0.1);
  color: var(--jd-orange-dark);
}

.jd-command__body {
  display: grid;
  gap: 22px;
  grid-template-columns: minmax(0, 1fr) 280px;
  padding: 24px;
}

.jd-kicker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.jd-kicker span {
  background: #f8fafc;
  border: 1px solid var(--jd-border);
  border-radius: 999px;
  padding: 7px 10px;
}

.jd-kicker span:first-child {
  background: var(--jd-soft);
  border-color: #fed7aa;
  color: var(--jd-orange-dark);
}

.jd-command h1 {
  color: var(--jd-ink);
  font-size: 34px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.12;
  margin: 0;
}

.jd-command p {
  color: var(--jd-muted);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.6;
  margin: 10px 0 0;
  max-width: 900px;
}

.jd-client-command__body {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 0.9fr) minmax(520px, 1.4fr);
  padding: 24px;
}

.jd-client-heading {
  align-self: center;
}

.jd-client-heading h1 {
  color: var(--jd-ink);
  font-size: 32px;
  font-weight: 900;
  line-height: 1.15;
  margin: 0;
}

.jd-client-heading p {
  color: var(--jd-muted);
  font-size: 14px;
  font-weight: 800;
  line-height: 1.55;
  margin: 10px 0 0;
}

.jd-client-metrics {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.jd-client-metrics article,
.jd-client-route-card,
.jd-client-note {
  background: #ffffff;
  border: 1px solid var(--jd-border);
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(23, 32, 51, 0.05);
}

.jd-client-metrics article {
  min-height: 116px;
  padding: 15px;
}

.jd-client-metrics span,
.jd-client-route-card span,
.jd-client-note span,
.jd-client-route-card__header span {
  color: var(--jd-muted);
  display: block;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.jd-client-metrics strong,
.jd-client-note strong,
.jd-client-route-card__header strong {
  color: var(--jd-ink);
  display: block;
  font-size: 16px;
  font-weight: 900;
  line-height: 1.28;
  margin-top: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.jd-client-metrics small,
.jd-client-note small {
  color: var(--jd-muted);
  display: block;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.4;
  margin-top: 7px;
}

.jd-status-console {
  align-items: flex-start;
  background: #f8fafc;
  border: 1px solid var(--jd-border);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 150px;
  padding: 18px;
}

.jd-status-pill {
  align-items: center;
  border-radius: 999px;
  cursor: default !important;
  display: inline-flex;
  font-size: 12px;
  font-weight: 900;
  gap: 7px;
  padding: 8px 12px;
  user-select: none;
}

.jd-status-pill::before {
  border-radius: 999px;
  content: "";
  cursor: default !important;
  height: 7px;
  pointer-events: none;
  width: 7px;
}

.jd-status-pill *,
.job-profile-status-value,
.job-profile-status-value * {
  caret-color: transparent !important;
  cursor: default !important;
  user-select: none;
}

/*
 * Status labels are static text. Keep one stable pointer target across the
 * label, its dot/pseudo-elements, and the surrounding details cell so the
 * browser cannot alternate between text/default cursors while hovering.
 */
.job-dossier .jd-status-pill,
.job-dossier .jd-status-pill *,
.job-dossier .job-profile-status-cell,
.job-dossier .job-profile-status-cell *,
.job-dossier .job-profile-status-value,
.job-dossier .job-profile-status-value * {
  caret-color: transparent !important;
  cursor: default !important;
}

.job-dossier .jd-status-pill,
.job-dossier .job-profile-status-value {
  pointer-events: auto !important;
  user-select: none !important;
}

.job-dossier .jd-status-pill::before,
.job-dossier .jd-status-pill::after,
.job-dossier .job-profile-status-value::before,
.job-dossier .job-profile-status-value::after {
  cursor: default !important;
  pointer-events: none !important;
}

.jd-status-pill--open {
  background: #fff7ed;
  color: #c2410c;
}

.jd-status-pill--open::before {
  background: var(--jd-orange);
}

.jd-status-pill--moving {
  background: #eef4ff;
  color: var(--jd-blue);
}

.jd-status-pill--moving::before {
  background: var(--jd-blue);
}

.jd-status-pill--pending {
  background: #fffbeb;
  color: #b45309;
}

.jd-status-pill--pending::before {
  background: #f59e0b;
}

.jd-status-pill--done {
  background: #ecfdf5;
  color: var(--jd-green);
}

.jd-status-pill--done::before {
  background: var(--jd-green);
}

.jd-status-pill--cancelled {
  background: #fef3f2;
  color: var(--jd-red);
}

.jd-status-pill--cancelled::before {
  background: var(--jd-red);
}

.jd-status-pill--neutral {
  background: #eef2f7;
  color: #475467;
}

.jd-status-console strong {
  color: var(--jd-ink);
  display: block;
  font-size: 22px;
  font-weight: 900;
  margin-top: 16px;
}

.jd-status-console small {
  color: var(--jd-muted);
  display: block;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.45;
  margin-top: 5px;
}

.jd-intelligence {
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(360px, 1.4fr) repeat(3, minmax(0, 1fr));
}

.jd-client-overview {
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1.6fr) 320px;
}

.jd-route-board,
.jd-intel-card,
.jd-progress,
.jd-section {
  background: var(--jd-panel);
  border: 1px solid var(--jd-border);
  border-radius: 12px;
  box-shadow: 0 10px 26px rgba(23, 32, 51, 0.05);
}

.jd-route-board {
  padding: 16px;
}

.jd-client-route-card {
  padding: 18px;
}

.jd-client-route-card__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
}

.jd-client-route-card__header strong {
  margin-top: 0;
}

.jd-client-route {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) 140px minmax(0, 1fr);
}

.jd-client-route strong {
  color: var(--jd-ink);
  display: block;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.35;
  margin-top: 6px;
  overflow-wrap: anywhere;
}

.jd-client-note {
  align-content: center;
  display: grid;
  min-height: 156px;
  padding: 18px;
}

.jd-route-board__map {
  align-items: center;
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1fr) 120px minmax(0, 1fr);
  margin-top: 13px;
}

.jd-route-board__map span,
.jd-intel-card small {
  color: var(--jd-muted);
  display: block;
  font-size: 12px;
  font-weight: 800;
}

.jd-route-board__map strong {
  color: var(--jd-ink);
  display: block;
  font-size: 16px;
  font-weight: 900;
  line-height: 1.35;
  margin-top: 5px;
  overflow-wrap: anywhere;
}

.jd-route-rail {
  align-items: center;
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr auto 1fr;
}

.jd-route-rail span {
  background: #fed7aa;
  border-radius: 999px;
  height: 3px;
}

.jd-route-rail .v-icon {
  background: var(--jd-soft);
  border: 1px solid #fed7aa;
  border-radius: 999px;
  color: var(--jd-orange-dark);
  padding: 8px;
}

.jd-intel-card {
  min-height: 128px;
  padding: 16px;
}

.jd-intel-card strong {
  color: var(--jd-ink);
  display: block;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.25;
  margin-top: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.jd-intel-card small {
  margin-top: 7px;
}

.jd-progress {
  display: grid;
  gap: 0;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  overflow: hidden;
}

.jd-progress-step {
  background: #ffffff;
  border-right: 1px solid var(--jd-border);
  min-height: 76px;
  padding: 15px 16px;
  position: relative;
}

.jd-progress-step:last-child {
  border-right: 0;
}

.jd-progress-step span {
  background: #d0d5dd;
  border-radius: 999px;
  display: block;
  height: 8px;
  margin-bottom: 10px;
  width: 8px;
}

.jd-progress-step small {
  color: #8a94a6;
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-top: 4px;
}

.jd-progress-step--complete span,
.jd-progress-step--active span {
  background: var(--jd-orange);
}

.jd-progress-step--active {
  background: #fffaf5;
}

.jd-progress-step--active strong {
  color: var(--jd-orange-dark);
}

.jd-section {
  overflow: hidden;
}

.jd-section-header {
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid var(--jd-border);
  display: flex;
  justify-content: space-between;
  padding: 18px 20px;
}

.jd-section-header h2 {
  color: var(--jd-ink);
  font-size: 20px;
  font-weight: 900;
  line-height: 1.25;
  margin: 4px 0 0;
}

.jd-section-header small {
  background: #f8fafc;
  border: 1px solid var(--jd-border);
  border-radius: 999px;
  color: var(--jd-orange-dark);
  font-size: 12px;
  font-weight: 900;
  padding: 8px 11px;
}

.jd-section--profile > .job-detail-profile-card,
.jd-section--profile > div > .job-detail-profile-card {
  border: 0;
}

.jd-stepper {
  background: transparent !important;
  box-shadow: none !important;
}

.jd-lane-tabs {
  background: #f8fafc !important;
  border-bottom: 1px solid var(--jd-border);
  box-shadow: none !important;
  min-height: 74px;
  overflow: hidden;
}

.jd-lane-tabs .v-stepper__step {
  border-right: 1px solid var(--jd-border);
  min-height: 74px;
  min-width: 150px;
  padding: 14px 18px;
}

.jd-lane-tabs .v-stepper__step span {
  color: var(--jd-ink);
  display: block;
  font-size: 13px;
  font-weight: 900;
}

.jd-lane-tabs .v-stepper__step small {
  color: var(--jd-muted);
  display: block;
  font-size: 11px;
  font-weight: 900;
  margin-top: 4px;
  text-transform: uppercase;
}

.jd-lane-tabs .v-stepper__step--active,
.jd-lane-tabs .v-stepper__step--complete {
  background: #ffffff !important;
}

.jd-stepper .v-stepper__items,
.jd-stepper .v-stepper__content {
  background: var(--jd-bg) !important;
  padding: 16px !important;
}

.jd-lane-workbench {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1fr) 360px;
}

.jd-lane-workbench__main,
.jd-lane-workbench__quote,
.jd-assignments-panel {
  min-width: 0;
}

.jd-lane-workbench__quote,
.jd-assignments-panel {
  background: #ffffff;
  border: 1px solid var(--jd-border);
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(23, 32, 51, 0.05);
  overflow: hidden;
}

.jd-mini-heading {
  align-items: center;
  background: #fbfcfe;
  border-bottom: 1px solid var(--jd-border);
  display: flex;
  justify-content: space-between;
  min-height: 54px;
  padding: 12px 16px;
}

.jd-mini-heading strong {
  color: var(--jd-ink);
  font-size: 14px;
  font-weight: 900;
}

.jd-lane-workbench__quote > div > .v-card,
.jd-lane-workbench__quote .v-card,
.jd-assignments-panel > .v-card {
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.jd-lane-workbench__quote .p-16 {
  padding: 14px 16px 18px !important;
}

.jd-lane-workbench__quote .v-divider,
.jd-lane-workbench__quote .heading.orange--text {
  display: none;
}

.jd-lane-workbench__quote .layout.row {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr !important;
}

.jd-lane-workbench__quote .layout.row > .flex {
  flex-basis: auto !important;
  max-width: none !important;
  width: auto !important;
}

.jd-lane-workbench__quote .layout.row > .flex.xs1 {
  display: none !important;
}

.jd-lane-workbench__quote .v-text-field {
  margin-top: 0;
  padding-top: 0;
}

.jd-lane-workbench__quote .v-input input {
  color: var(--jd-ink);
  font-size: 14px;
  font-weight: 900;
}

.jd-assignments-panel {
  margin-top: 14px;
}

.jd-assignments-panel .v-card__title.bg-clr {
  background: #ffffff !important;
  border-bottom: 1px solid var(--jd-border);
  min-height: 58px;
}

.jd-assignments-panel #dataTable .v-table__overflow {
  border: 0;
}

.jd-assignments-panel #dataTable table.v-table thead th {
  background: #f8fafc !important;
  color: #667085 !important;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.jd-assignments-panel #dataTable table.v-table tbody td {
  border-bottom: 1px solid #edf1f7 !important;
  color: #2f3744;
  font-size: 12px !important;
}

.job-dossier .v-btn,
.job-dossier button.track-ass-btn,
.job-dossier .orange-button,
.job-dossier .communicate-btn {
  border-radius: 8px !important;
  box-shadow: none !important;
  font-weight: 900 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.job-dossier {
  padding: 14px 18px 24px !important;
}

.job-dossier .jd-command,
.job-dossier .jd-intelligence,
.job-dossier .jd-client-overview,
.job-dossier .jd-progress,
.job-dossier .jd-section {
  margin-bottom: 10px;
}

.job-dossier .jd-command {
  box-shadow: 0 10px 24px rgba(23, 32, 51, 0.06);
}

.job-dossier .jd-command__nav {
  min-height: 44px;
  padding: 7px 12px;
}

.job-dossier .jd-back {
  min-height: 30px;
  padding: 5px 10px;
}

.job-dossier .jd-view-switch button {
  min-height: 28px;
  padding: 5px 12px;
}

.job-dossier .jd-command__body,
.job-dossier .jd-client-command__body {
  gap: 14px;
  padding: 16px;
}

.job-dossier .jd-command__body {
  grid-template-columns: minmax(0, 1fr) 240px;
}

.job-dossier .jd-kicker {
  margin-bottom: 8px;
}

.job-dossier .jd-kicker span {
  padding: 5px 9px;
}

.job-dossier .jd-command h1,
.job-dossier .jd-client-heading h1 {
  font-size: 27px;
}

.job-dossier .jd-command p,
.job-dossier .jd-client-heading p {
  line-height: 1.42;
  margin-top: 7px;
}

.job-dossier .jd-status-console {
  min-height: 112px;
  padding: 14px;
}

.job-dossier .jd-status-console strong {
  font-size: 18px;
  margin-top: 10px;
}

.job-dossier .jd-client-metrics article,
.job-dossier .jd-intel-card {
  min-height: 86px;
  padding: 11px;
}

.job-dossier .jd-intel-card strong,
.job-dossier .jd-client-metrics strong {
  margin-top: 6px;
}

.job-dossier .jd-progress-step {
  min-height: 56px;
  padding: 10px 12px;
}

.job-dossier .jd-progress-step span {
  margin-bottom: 7px;
}

.job-dossier .jd-section-header {
  padding: 12px 14px;
}

.job-dossier .jd-section-header h2 {
  font-size: 17px;
}

.job-dossier .jd-section-header small {
  padding: 6px 9px;
}

.job-dossier .jd-lane-tabs {
  min-height: 58px;
}

.job-dossier .jd-lane-tabs .v-stepper__step {
  min-height: 58px;
  padding: 10px 14px;
}

.job-dossier .jd-stepper .v-stepper__content,
.job-dossier .jd-stepper .v-stepper__items {
  padding: 10px !important;
}

.job-dossier .jd-lane-workbench {
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) 320px;
}

.job-dossier .jd-mini-heading {
  min-height: 44px;
  padding: 9px 12px;
}

.job-dossier .jd-lane-workbench__quote .p-16 {
  padding: 10px 12px 12px !important;
}

.job-dossier .jd-assignments-panel {
  margin-top: 10px;
}

.job-dossier .jd-assignments-panel .v-card__title.bg-clr {
  min-height: 46px;
}

.job-dossier {
  background: #f5f6f8;
  padding: 10px 14px 18px !important;
}

.job-dossier .jd-command--flat {
  border-radius: 8px;
  box-shadow: none;
  margin-bottom: 8px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.job-dossier .jd-command__nav--flat {
  background: #ffffff;
  border-bottom: 0;
  gap: 12px;
  min-height: 42px;
  padding: 6px 10px;
}

.jd-job-strip {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  gap: 10px;
  min-width: 0;
}

.jd-job-strip strong {
  color: var(--jd-ink);
  flex: 0 0 auto;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.2;
}

.jd-job-strip small {
  border-left: 1px solid var(--jd-border);
  color: var(--jd-muted);
  display: inline-block;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.2;
  min-width: 0;
  overflow: hidden;
  padding-left: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.jd-job-strip .jd-status-pill {
  flex: 0 0 auto;
  font-size: 11px;
  padding: 5px 9px;
}

.jd-job-strip .jd-status-pill::before {
  height: 6px;
  width: 6px;
}

.job-dossier .jd-view-switch {
  background: #f3f4f6;
  border-radius: 8px;
  flex: 0 0 auto;
}

.job-dossier .jd-view-switch button {
  border-radius: 6px;
  font-size: 11px;
  min-height: 26px;
  padding: 4px 10px;
}

.job-dossier .jd-section {
  border-radius: 8px;
  box-shadow: none;
  margin-bottom: 8px;
}

.job-dossier .jd-section--profile {
  border: 1px solid var(--jd-border);
}

.job-dossier .jd-section--lanes {
  background: transparent;
  border: 0;
  overflow: visible;
}

.job-dossier .jd-lane-tabs {
  background: #ffffff !important;
  border: 1px solid var(--jd-border);
  border-radius: 8px 8px 0 0;
  min-height: 48px;
}

.job-dossier .jd-lane-tabs .v-stepper__step {
  min-height: 48px;
  min-width: 132px;
  padding: 8px 12px;
}

.job-dossier .jd-lane-tabs .v-stepper__step span {
  font-size: 12px;
}

.job-dossier .jd-lane-tabs .v-stepper__step small {
  font-size: 10px;
}

.job-dossier .jd-stepper .v-stepper__content,
.job-dossier .jd-stepper .v-stepper__items {
  background: transparent !important;
  padding: 8px 0 0 !important;
}

.job-dossier .jd-lane-workbench {
  gap: 8px;
  grid-template-columns: minmax(0, 1fr) 304px;
}

.job-dossier .jd-lane-workbench__quote,
.job-dossier .jd-assignments-panel {
  border-radius: 8px;
  box-shadow: none;
}

.job-dossier .jd-mini-heading {
  background: #ffffff;
  min-height: 38px;
  padding: 7px 10px;
}

.job-dossier .jd-mini-heading strong {
  font-size: 13px;
}

.job-dossier .jd-assignments-panel {
  margin-top: 8px;
}

.job-dossier .jd-assignments-panel .v-card__title.bg-clr {
  min-height: 40px;
}

.job-dossier--modern .jd-modern-workspace,
.job-dossier--modern .jd-section,
.job-dossier--modern .jd-stepper,
.job-dossier--modern .jd-stepper .v-stepper__items,
.job-dossier--modern .jd-stepper .v-stepper__content,
.job-dossier--modern .jd-lane-workbench,
.job-dossier--modern .jd-assignments-panel,
.job-dossier--modern .jd-assignments-panel #dataTable,
.job-dossier--modern .jd-assignments-panel #dataTable .v-table__overflow {
  max-height: none !important;
  overflow: visible !important;
}

.job-dossier--modern .jd-assignments-panel #dataTable .v-table__overflow {
  overflow-x: auto !important;
  overflow-y: visible !important;
}

.job-dossier--modern .jd-assignments-panel #dataTable table.v-table {
  min-width: 0 !important;
  table-layout: fixed;
  width: 100% !important;
}

.job-dossier--modern .jd-assignments-panel #dataTable table.v-table th,
.job-dossier--modern .jd-assignments-panel #dataTable table.v-table td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-break: break-word;
}

.job-dossier--clients {
  background: #f7f8fb;
  padding: 12px 18px 28px !important;
}

.jd-client-file {
  color: #172033;
  display: grid;
  gap: 18px;
  margin: 0 auto;
  max-width: 1500px;
  width: 100%;
}

.jd-client-hero {
  align-items: center;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 20px 2px 4px;
}

.jd-client-hero span,
.jd-client-section-title span,
.jd-client-kv-grid span,
.jd-client-subsection > span,
.jd-client-manifest small,
.jd-client-finance-rows span {
  color: #7a8494;
  display: block;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.2;
  text-transform: uppercase;
}

.jd-client-hero h1 {
  color: #111827;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.12;
  margin: 5px 0 0;
}

.jd-client-hero p {
  color: #667085;
  font-size: 14px;
  font-weight: 800;
  margin: 8px 0 0;
}

.jd-client-download {
  align-items: center;
  background: #f58220;
  border: 0;
  border-radius: 8px;
  color: #ffffff !important;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 900;
  gap: 8px;
  min-height: 38px;
  padding: 9px 14px;
  text-decoration: none;
}

.jd-client-download[disabled] {
  background: #e5e7eb;
  color: #667085 !important;
  cursor: default;
}

.jd-client-download .v-icon {
  color: currentColor !important;
}

.jd-client-track {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  padding: 18px 4px 22px;
  position: relative;
}

.jd-client-track::before {
  background: #dfe4ec;
  content: "";
  height: 2px;
  left: 7%;
  position: absolute;
  right: 7%;
  top: 27px;
}

.jd-client-track__step {
  position: relative;
  text-align: center;
  z-index: 1;
}

.jd-client-track__step span {
  background: #ffffff;
  border: 2px solid #cfd6e2;
  border-radius: 999px;
  display: block;
  height: 18px;
  margin: 0 auto 8px;
  width: 18px;
}

.jd-client-track__step strong {
  color: #667085;
  display: block;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.2;
}

.jd-client-track__step--complete span {
  background: #16a66a;
  border-color: #16a66a;
}

.jd-client-track__step--active span {
  background: #f58220;
  border-color: #f58220;
  box-shadow: 0 0 0 5px #fff1e5;
}

.jd-client-track__step--active strong {
  color: #111827;
}

.jd-client-grid {
  align-items: start;
  display: grid;
  gap: 28px;
  grid-template-columns: minmax(0, 3fr) minmax(320px, 2fr);
}

.jd-client-overview,
.jd-client-finance {
  min-width: 0;
}

.jd-client-section-title {
  border-bottom: 1px solid #e7ebf2;
  margin-bottom: 12px;
  padding-bottom: 10px;
}

.jd-client-section-title strong {
  color: #111827;
  display: block;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.2;
  margin-top: 3px;
  overflow-wrap: anywhere;
}

.jd-client-kv-grid,
.jd-client-finance-rows {
  display: grid;
  gap: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.jd-client-kv-grid > div,
.jd-client-finance-rows > div {
  border-bottom: 1px solid #edf1f6;
  display: grid;
  gap: 4px;
  min-height: 46px;
  padding: 8px 10px 8px 0;
}

.jd-client-kv-grid strong,
.jd-client-manifest strong,
.jd-client-finance-rows strong {
  color: #111827;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.jd-client-subsection {
  margin-top: 18px;
}

.jd-client-manifest,
.jd-client-driver-list {
  display: grid;
  gap: 0;
  margin-top: 8px;
}

.jd-client-manifest {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.jd-client-manifest > div,
.jd-client-driver-list > div {
  border-bottom: 1px solid #edf1f6;
  min-height: 42px;
  padding: 7px 10px 7px 0;
}

.jd-client-driver-list {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.jd-client-driver-list strong {
  color: #111827;
  display: block;
  font-size: 13px;
  font-weight: 900;
}

.jd-client-driver-list small,
.jd-client-empty {
  color: #667085;
  display: block;
  font-size: 12px;
  font-weight: 800;
  margin-top: 3px;
}

.jd-client-finance {
  background: transparent;
  border-left: 1px solid #e7ebf2;
  padding: 0 0 0 24px;
}

.jd-client-payment-badge {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 12px;
  font-weight: 900;
  margin-bottom: 12px;
  padding: 7px 10px;
}

.jd-client-payment-badge--done {
  background: #ecfdf5;
  color: #047857;
}

.jd-client-payment-badge--pending,
.jd-client-payment-badge--open {
  background: #fff7ed;
  color: #b45309;
}

.jd-client-payment-badge--moving {
  background: #eef4ff;
  color: #3157d5;
}

.jd-client-payment-badge--cancelled {
  background: #fef3f2;
  color: #b42318;
}

.jd-compact-route-strip {
  align-items: center;
  background: #ffffff;
  border: 1px solid var(--jd-border);
  border-radius: 8px;
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(180px, 0.8fr) minmax(360px, 2fr) minmax(180px, 0.8fr);
  margin-bottom: 8px;
  overflow: hidden;
  padding: 10px 12px;
}

.jd-compact-route-strip__point span,
.jd-compact-step small,
.jd-pane-title span,
.jd-master-table span,
.jd-stakeholder-list span {
  color: #7a8494;
  display: block;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.2;
  text-transform: uppercase;
}

.jd-compact-route-strip__point strong,
.jd-pane-title strong,
.jd-master-table strong,
.jd-stakeholder-list strong {
  color: #111827;
  display: block;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.25;
  margin-top: 4px;
  overflow-wrap: anywhere;
}

.jd-compact-stepper {
  align-items: start;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  position: relative;
}

.jd-compact-stepper::before {
  background: #d9e0ea;
  content: "";
  height: 2px;
  left: 10%;
  position: absolute;
  right: 10%;
  top: 12px;
}

.jd-compact-step {
  min-width: 0;
  position: relative;
  text-align: center;
  z-index: 1;
}

.jd-compact-step > span {
  background: #ffffff;
  border: 2px solid #cfd6e2;
  border-radius: 999px;
  display: block;
  height: 16px;
  margin: 5px auto 6px;
  width: 16px;
}

.jd-compact-step .v-icon {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 999px;
  color: var(--jd-orange-dark) !important;
  left: 50%;
  padding: 5px;
  position: absolute;
  top: -3px;
  transform: translateX(-50%);
}

.jd-compact-step--complete > span {
  background: #16a66a;
  border-color: #16a66a;
}

.jd-compact-step--active > span {
  border-color: transparent;
}

.job-dossier--modern .jd-section,
.job-dossier--modern .jd-stepper,
.job-dossier--modern .jd-stepper .v-stepper__items,
.job-dossier--modern .jd-stepper .v-stepper__content,
.job-dossier--modern .jd-assignments-panel,
.job-dossier--modern .assignments-table-shell,
.job-dossier--modern #dataTable,
.job-dossier--modern #dataTable .v-table__overflow {
  max-height: none !important;
  overflow: visible !important;
}

.job-dossier--modern .assignment-focus-shell {
  max-height: none !important;
  overflow: visible !important;
}

.job-dossier--modern #dataTable .v-table__overflow {
  overflow-x: auto !important;
  overflow-y: visible !important;
}

.job-dossier--modern.job-dossier {
  box-sizing: border-box;
  max-width: none !important;
  padding: 0.5rem 0.5rem 1.125rem !important;
  width: 100% !important;
}

.job-dossier .v-icon {
  animation: none !important;
  backface-visibility: hidden;
  transition: none !important;
  will-change: auto !important;
}

.job-dossier--modern .jd-command--flat {
  margin-bottom: 0.5rem;
}

.job-dossier--modern .jd-command__nav--flat {
  min-height: 2.25rem;
  padding: 0.3125rem 0.625rem;
}

.job-detail-profile-card .payer-strip {
  align-items: start;
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  min-height: 0;
  padding: 0 !important;
}

.job-detail-profile-card .payer-strip__item {
  align-content: start;
  background: transparent !important;
  border-right: 1px solid #eef1f6;
  min-height: 0;
  padding: 0 8px 0 0 !important;
}

.job-detail-profile-card .payer-strip__item:last-child {
  border-right: 0;
  padding: 0 !important;
}

.job-detail-profile-card .payer-strip__radio .v-input__slot,
.job-detail-profile-card .payer-strip__radio .v-messages {
  margin: 0 !important;
  min-height: 0 !important;
}

.job-dossier--clients .jd-client-track {
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 16px 38px rgba(23, 32, 51, 0.06);
}

.job-dossier--clients .jd-client-track__step span {
  align-items: center;
  display: flex;
  justify-content: center;
}

.job-dossier--clients .jd-client-track__step span .v-icon {
  color: #ffffff !important;
  font-size: 13px !important;
}

.job-dossier--clients .jd-client-overview,
.job-dossier--clients .jd-client-finance {
  background: #ffffff;
  border: 0;
  border-radius: 16px;
  box-shadow: 0 18px 44px rgba(23, 32, 51, 0.08);
  padding: 18px;
}

.job-dossier--clients .jd-client-grid {
  align-items: start;
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  width: 100%;
}

.jd-bento-assignments {
  background: #ffffff;
  border: 0;
  border-radius: 16px;
  box-shadow: 0 18px 44px rgba(23, 32, 51, 0.08);
  padding: 18px;
  width: 100%;
}

.job-dossier--clients .jd-stepper--bento,
.job-dossier--clients .jd-stepper--bento .v-stepper__items,
.job-dossier--clients .jd-stepper--bento .v-stepper__content {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
}

.job-dossier--clients .jd-stepper--bento .jd-lane-tabs {
  border: 1px solid #edf1f6;
  border-radius: 10px;
  margin-bottom: 10px;
  min-height: 42px;
}

.job-dossier--clients .jd-stepper--bento .v-stepper__step {
  min-height: 42px;
  min-width: 108px;
  padding: 8px 10px;
}

.job-dossier--clients .assignments-modern-card {
  border: 0 !important;
  box-shadow: none !important;
}

.job-dossier--clients .assignment-focus-shell {
  background: #f8fafc;
  border: 0;
  border-top: 1px solid #e5eaf2;
  box-shadow: none;
  margin: 0;
  max-height: none;
  max-width: none;
  overflow: visible;
  padding: 14px;
  position: static;
  width: 100%;
  z-index: auto;
}

.job-dossier--clients .assignment-focus-header {
  position: static;
  z-index: auto;
}

.job-dossier--clients .jd-job-strip small,
.job-dossier--clients .jd-client-section-title strong,
.job-dossier--clients .jd-client-kv-grid strong,
.job-dossier--clients .jd-client-manifest strong,
.job-dossier--clients .jd-client-driver-list strong,
.job-dossier--clients .jd-client-finance-rows strong,
.job-dossier--masterDetail .jd-job-strip small,
.job-dossier--masterDetail .jd-pane-title strong,
.job-dossier--masterDetail .jd-master-table strong,
.job-dossier--masterDetail .jd-stakeholder-list strong {
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: normal !important;
  word-break: normal;
  overflow-wrap: anywhere;
}

.job-dossier--clients .jd-client-hero span,
.job-dossier--clients .jd-client-section-title span,
.job-dossier--clients .jd-client-kv-grid span,
.job-dossier--clients .jd-client-subsection > span,
.job-dossier--clients .jd-client-manifest small,
.job-dossier--clients .jd-client-finance-rows span,
.job-dossier--masterDetail .jd-pane-title span,
.job-dossier--masterDetail .jd-master-table span,
.job-dossier--masterDetail .jd-stakeholder-list span {
  color: #596579;
  font-size: 12px;
  line-height: 1.25;
}

.job-dossier--clients .jd-client-kv-grid,
.job-dossier--clients .jd-client-finance-rows {
  grid-template-columns: 1fr;
}

.job-dossier--clients .jd-client-manifest,
.job-dossier--clients .jd-client-driver-list {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.job-dossier--clients .assignments-table-shell,
.job-dossier--clients #dataTable,
.job-dossier--clients #dataTable .v-table__overflow,
.job-dossier--masterDetail .assignments-table-shell,
.job-dossier--masterDetail #dataTable,
.job-dossier--masterDetail #dataTable .v-table__overflow {
  max-height: none !important;
  overflow: visible !important;
  width: 100% !important;
}

.job-dossier--clients #dataTable table.v-table,
.job-dossier--masterDetail #dataTable table.v-table {
  display: block;
  min-width: 0 !important;
  table-layout: auto;
  width: 100% !important;
}

.job-dossier--clients #dataTable table.v-table thead,
.job-dossier--clients #dataTable table.v-table tbody,
.job-dossier--masterDetail #dataTable table.v-table thead,
.job-dossier--masterDetail #dataTable table.v-table tbody {
  display: block;
  width: 100%;
}

.job-dossier--clients #dataTable table.v-table thead tr,
.job-dossier--clients #dataTable table.v-table tbody tr.assignment-row,
.job-dossier--masterDetail #dataTable table.v-table thead tr,
.job-dossier--masterDetail #dataTable table.v-table tbody tr.assignment-row {
  align-items: stretch;
  border-bottom: 1px solid #edf1f6;
  display: grid;
  gap: 0;
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  width: 100%;
}

.job-dossier--masterDetail #dataTable table.v-table thead tr,
.job-dossier--masterDetail #dataTable table.v-table tbody tr.assignment-row {
  grid-template-columns: repeat(auto-fit, minmax(118px, 1fr));
}

.job-dossier--clients #dataTable table.v-table tbody tr:not(.assignment-row),
.job-dossier--masterDetail #dataTable table.v-table tbody tr:not(.assignment-row) {
  display: block;
  width: 100%;
}

.job-dossier--clients #dataTable table.v-table th,
.job-dossier--clients #dataTable table.v-table td,
.job-dossier--masterDetail #dataTable table.v-table th,
.job-dossier--masterDetail #dataTable table.v-table td {
  display: block;
  font-size: 12px !important;
  line-height: 1.35 !important;
  min-width: 0;
  overflow: visible !important;
  padding: 9px 10px !important;
  text-overflow: clip !important;
  white-space: normal !important;
  word-break: normal;
  overflow-wrap: anywhere;
}

.job-dossier--clients #dataTable table.v-table th,
.job-dossier--masterDetail #dataTable table.v-table th {
  color: #596579 !important;
  font-weight: 900 !important;
}

.job-dossier--clients #dataTable .v-btn,
.job-dossier--clients #dataTable button,
.job-dossier--masterDetail #dataTable .v-btn,
.job-dossier--masterDetail #dataTable button {
  white-space: normal !important;
}

.jd-master-detail {
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(220px, 20%) minmax(320px, 30%) minmax(620px, 50%);
  min-height: 0;
}

.job-dossier--masterDetail {
  background: #f6f7f9;
  padding: 10px 14px 18px !important;
}

.jd-master-pane {
  background: #ffffff;
  border: 1px solid #e5eaf2;
  border-radius: 12px;
  min-width: 0;
  overflow: visible;
}

.jd-pane-title {
  border-bottom: 1px solid #edf1f6;
  padding: 12px 14px;
}

.jd-vertical-route {
  padding: 14px;
  position: relative;
}

.jd-vertical-route::before {
  background: #dce3ed;
  bottom: 28px;
  content: "";
  left: 24px;
  position: absolute;
  top: 28px;
  width: 2px;
}

.jd-vertical-route__step {
  display: grid;
  gap: 10px;
  grid-template-columns: 22px minmax(0, 1fr);
  min-height: 92px;
  position: relative;
  z-index: 1;
}

.jd-vertical-route__step > span {
  align-items: center;
  background: #ffffff;
  border: 2px solid #cfd6e2;
  border-radius: 999px;
  display: flex;
  height: 22px;
  justify-content: center;
  width: 22px;
}

.jd-vertical-route__step--complete > span {
  background: #16a66a;
  border-color: #16a66a;
}

.jd-vertical-route__step--active > span {
  background: var(--jd-orange);
  border-color: var(--jd-orange);
}

.jd-vertical-route__step .v-icon {
  color: #ffffff !important;
  font-size: 13px !important;
}

.jd-vertical-route__step strong {
  color: #111827;
  display: block;
  font-size: 13px;
  font-weight: 900;
}

.jd-vertical-route__step small {
  color: #667085;
  display: block;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
  margin-top: 4px;
  overflow-wrap: anywhere;
}

.jd-master-table,
.jd-stakeholder-list {
  display: grid;
  gap: 0;
  padding: 0 14px 14px;
}

.jd-master-table {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.jd-master-table > div,
.jd-stakeholder-list > div {
  border-bottom: 1px solid #edf1f6;
  min-height: 42px;
  padding: 8px 10px 8px 0;
}

.job-dossier--masterDetail .job-detail-profile-card .job-profile-shell,
.job-dossier--masterDetail .job-detail-profile-card .job-info-panel,
.job-dossier--masterDetail .assignments-modern-card {
  border: 0 !important;
  box-shadow: none !important;
}

.job-dossier--masterDetail .job-detail-profile-card .job-profile-actions,
.job-dossier--masterDetail .assignments-modern-card .v-card__title.bg-clr {
  background: transparent !important;
}

.job-dossier--masterDetail .job-detail-profile-card .job-info-panel {
  border-top: 1px solid #edf1f6 !important;
  border-radius: 0 !important;
  padding-left: 14px !important;
  padding-right: 14px !important;
}

.job-dossier--masterDetail .jd-stepper--master,
.job-dossier--masterDetail .jd-stepper--master .v-stepper__items,
.job-dossier--masterDetail .jd-stepper--master .v-stepper__content {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
}

.job-dossier--masterDetail .jd-stepper--master .jd-lane-tabs {
  border: 0;
  border-bottom: 1px solid #edf1f6;
  border-radius: 0;
  min-height: 42px;
}

.job-dossier--masterDetail .jd-stepper--master .v-stepper__step {
  min-height: 42px;
  min-width: 94px;
  padding: 8px 10px;
}

.job-dossier--masterDetail .assignment-focus-shell {
  background: #f8fafc;
  border: 0;
  border-top: 1px solid #edf1f6;
  box-shadow: none;
  margin: 0;
  max-height: none;
  overflow: visible;
  padding: 10px;
  width: 100%;
}

.job-dossier--masterDetail .assignment-focus-header {
  background: transparent;
  border-bottom: 1px solid #edf1f6;
  margin-bottom: 8px;
  padding-bottom: 8px;
}

.job-dossier--masterDetail .job-profile-body .layout {
  display: grid !important;
  gap: 8px;
  grid-template-columns: 1fr !important;
}

.job-dossier--masterDetail .job-profile-body .flex {
  flex-basis: auto !important;
  max-width: none !important;
  width: 100% !important;
}

.job-dossier--masterDetail .job-profile-body .heading,
.job-dossier--masterDetail .job-profile-body .subheading {
  line-height: 1.35;
  white-space: normal !important;
  word-break: normal;
  overflow-wrap: anywhere;
}

@media (max-width: 1260px) {
  .jd-command__body,
  .jd-client-command__body,
  .jd-intelligence,
  .jd-client-overview,
  .jd-lane-workbench {
    grid-template-columns: 1fr;
  }

  .jd-client-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .jd-route-board__map,
  .jd-client-route {
    grid-template-columns: 1fr;
  }

  .jd-route-rail {
    display: none;
  }

  .jd-client-grid {
    grid-template-columns: 1fr;
  }

  .jd-compact-route-strip,
  .jd-master-detail {
    grid-template-columns: 1fr;
  }

  .jd-client-finance {
    border-left: 0;
    border-top: 1px solid #e7ebf2;
    padding: 18px 0 0;
  }
}

@media (max-width: 860px) {
  .job-dossier {
    padding: 14px !important;
  }

  .jd-progress,
  .jd-route-board__map {
    grid-template-columns: 1fr;
  }

  .jd-progress-step {
    border-bottom: 1px solid var(--jd-border);
    border-right: 0;
  }

  .jd-section-header,
  .jd-command__nav,
  .jd-command__body {
    align-items: flex-start;
    flex-direction: column;
  }

  .jd-view-switch {
    margin-left: 0;
    width: 100%;
  }

  .jd-view-switch button {
    flex: 1 1 0;
  }

  .jd-client-command__body {
    grid-template-columns: 1fr;
  }

  .jd-client-metrics {
    grid-template-columns: 1fr;
  }

  .jd-command h1 {
    font-size: 26px;
  }

  .jd-client-hero {
    align-items: flex-start;
    flex-direction: column;
    padding-top: 12px;
  }

  .jd-client-hero h1 {
    font-size: 25px;
  }

  .jd-client-download {
    justify-content: center;
    width: 100%;
  }

  .jd-client-track {
    gap: 12px;
    grid-template-columns: 1fr;
    padding: 10px 0;
  }

  .jd-client-track::before {
    bottom: 18px;
    height: auto;
    left: 8px;
    right: auto;
    top: 18px;
    width: 2px;
  }

  .jd-client-track__step {
    align-items: center;
    display: grid;
    gap: 10px;
    grid-template-columns: 18px minmax(0, 1fr);
    text-align: left;
  }

  .jd-client-track__step span {
    margin: 0;
  }

  .jd-client-kv-grid,
  .jd-client-finance-rows,
  .jd-client-manifest,
  .jd-client-driver-list {
    grid-template-columns: 1fr;
  }
}

.job-dossier--modern .jd-section--profile {
  background: transparent;
  border: 0;
  box-shadow: none;
  margin-bottom: 0.875rem;
  overflow: visible;
}

.job-dossier--modern .jd-compact-route-strip {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e7ebf2;
  border-radius: 8px;
  box-shadow: 0 14px 30px rgba(17, 24, 39, 0.05);
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(12rem, 0.85fr) minmax(20rem, 2fr) minmax(12rem, 0.85fr);
  margin: 0 0 0.875rem;
  overflow: visible;
  padding: 0.875rem 1rem;
}

.job-dossier--modern .jd-compact-route-strip__point {
  background: #fbfcff;
  border: 1px solid #edf1f6;
  border-radius: 8px;
  min-height: 4rem;
  padding: 0.625rem 0.75rem;
}

.job-dossier--modern .jd-compact-route-strip__point span {
  color: #7a8494;
  font-size: 0.65625rem;
}

.job-dossier--modern .jd-compact-route-strip__point strong {
  color: #111827;
  font-size: 0.8125rem;
  line-height: 1.3;
  margin-top: 0.25rem;
}

.job-dossier--modern .jd-compact-stepper {
  align-items: start;
  padding-top: 0.25rem;
}

.job-dossier--modern .jd-compact-stepper::before {
  background: #dfe4ec;
  left: 9%;
  right: 9%;
  top: 1rem;
}

.job-dossier--modern .jd-compact-step > span {
  border-color: #cfd6e2;
  height: 1rem;
  margin: 0.5rem auto 0.5rem;
  width: 1rem;
}

.job-dossier--modern .jd-compact-step--complete > span {
  background: #16a66a;
  border-color: #16a66a;
}

.job-dossier--modern .jd-compact-step--active > span {
  background: #f58220;
  border-color: #f58220;
  box-shadow: 0 0 0 0.3125rem #fff1e5;
}

.job-dossier--modern .jd-compact-step .v-icon {
  background: #f58220;
  border-color: #f58220;
  color: #ffffff !important;
  top: -0.125rem;
}

.job-dossier--modern .jd-compact-step small {
  color: #667085;
  font-size: 0.6875rem;
  line-height: 1.2;
}

.job-dossier--modern .jd-compact-step--active small {
  color: #111827;
}

@media (max-width: 1260px) {
  .job-dossier--modern .jd-compact-route-strip {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 860px) {
  .job-dossier--modern .jd-compact-stepper {
    gap: 0.75rem;
    grid-template-columns: 1fr;
    padding: 0;
  }

  .job-dossier--modern .jd-compact-stepper::before {
    bottom: 0.75rem;
    height: auto;
    left: 0.5rem;
    right: auto;
    top: 0.75rem;
    width: 2px;
  }

  .job-dossier--modern .jd-compact-step {
    align-items: center;
    display: grid;
    gap: 0.75rem;
    grid-template-columns: 1rem minmax(0, 1fr);
    text-align: left;
  }

  .job-dossier--modern .jd-compact-step > span {
    margin: 0;
  }

  .job-dossier--modern .jd-compact-step .v-icon {
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }
}

.job-dossier--modern.job-dossier {
  --jd-bg: #f7f3ec;
  --jd-panel: #ffffff;
  --jd-border: #e7e0d6;
  --jd-border-strong: #d8d0c4;
  --jd-ink: #171b26;
  --jd-muted: #777166;
  --jd-soft: #fff3e6;
  --jd-orange: #f58220;
  --jd-orange-dark: #b85d0d;
  --jd-navy: #183a91;
  --jd-navy-soft: #eef3ff;
  background: var(--jd-bg);
  color: var(--jd-ink);
  min-height: calc(100vh - 54px);
  box-sizing: border-box;
  max-width: none !important;
  padding: 0.5rem 0.5rem 1.125rem !important;
  width: 100% !important;
}

.job-dossier--modern .jd-modern-workspace,
.job-dossier--modern .jd-command--flat {
  margin-left: auto;
  margin-right: auto;
  max-width: none;
  width: calc(100% - 2rem);
}

.job-dossier--modern .jd-modern-workspace {
  display: grid;
  gap: 1rem;
}

.job-dossier--modern .jd-command--flat {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  margin-bottom: 0.75rem;
  overflow: visible;
  position: static;
}

.job-dossier--modern .jd-command__nav--flat {
  background: transparent;
  border: 0;
  gap: 0.75rem;
  min-height: 2rem;
  padding: 0;
}

.job-dossier--modern .jd-back {
  background: transparent;
  border: 0;
  border-radius: 0;
  color: #475467;
  gap: 0.375rem;
  min-height: 1.75rem;
  padding: 0;
}

.job-dossier--modern .jd-back .v-icon {
  color: currentColor !important;
}

.job-dossier--modern .jd-job-strip {
  background: #ffffff;
  border: 1px solid var(--jd-border);
  border-radius: 999px;
  flex: 0 1 auto;
  margin-left: auto;
  max-width: 100%;
  min-height: 2rem;
  padding: 0.25rem 0.625rem;
}

.job-dossier--modern .jd-job-strip strong {
  color: var(--jd-navy);
  font-size: 0.8125rem;
}

.job-dossier--modern .jd-job-strip small {
  border-left-color: var(--jd-border);
  color: var(--jd-muted);
  font-size: 0.71875rem;
}

.job-dossier--modern .jd-job-strip .jd-status-pill {
  font-size: 0.6875rem;
  padding: 0.25rem 0.5rem;
}

.job-dossier--modern .jd-section--profile {
  margin-bottom: 0;
}

.job-dossier--modern .jd-compact-route-strip {
  border-color: var(--jd-border);
  box-shadow: 0 1.125rem 2.25rem rgba(32, 24, 16, 0.07);
  margin-bottom: 0;
}

.job-dossier--modern .jd-compact-route-strip__point {
  background: #fbfaf7;
  border-color: #ece5da;
}

.job-dossier--modern .jd-compact-step--active > span {
  box-shadow: 0 0 0 0.3125rem #fff0de;
}

.job-dossier--modern .jd-section--lanes {
  background: #ffffff;
  border: 1px solid var(--jd-border);
  border-radius: 8px;
  box-shadow: 0 1.125rem 2.25rem rgba(32, 24, 16, 0.07);
  margin-bottom: 0;
  overflow: hidden !important;
  padding: 0;
}

.job-dossier--modern .jd-stepper {
  background: transparent !important;
  box-shadow: none !important;
}

.job-dossier--modern .jd-lane-tabs {
  align-items: center;
  background: #f2eee7 !important;
  border: 0;
  border-bottom: 1px solid var(--jd-border);
  border-radius: 0;
  box-shadow: none !important;
  gap: 0.625rem;
  min-height: 4rem;
  padding: 0.625rem 0.75rem;
}

.job-dossier--modern .jd-lane-tabs .v-stepper__step {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e5ded3;
  border-radius: 8px;
  color: #5f6673;
  flex: 0 0 auto;
  gap: 0.5rem;
  min-height: 2.75rem;
  min-width: 0;
  padding: 0.5625rem 0.875rem;
}

.job-dossier--modern .jd-lane-tabs .jd-lane-tab-icon {
  align-items: center;
  background: var(--jd-soft);
  border: 1px solid #fdba74;
  border-radius: 999px;
  color: var(--jd-orange-dark) !important;
  display: inline-flex !important;
  flex: 0 0 1.75rem;
  font-size: 1rem !important;
  height: 1.75rem;
  justify-content: center;
  margin-right: 0.125rem;
  width: 1.75rem;
}

.job-dossier--modern .jd-lane-tabs .v-stepper__step--active,
.job-dossier--modern .jd-lane-tabs .v-stepper__step--complete {
  background: #ffffff;
  border-color: #fed7aa;
  box-shadow: 0 0.25rem 0.75rem rgba(245, 130, 32, 0.14);
}

.job-dossier--modern .jd-lane-tabs .v-stepper__step span {
  color: #171b26;
  font-size: 0.75rem;
  font-weight: 900;
}

.job-dossier--modern .jd-lane-tabs .v-stepper__step__step {
  display: none !important;
}

.job-dossier--modern .jd-stepper .v-stepper__content,
.job-dossier--modern .jd-stepper .v-stepper__items {
  background: #ffffff !important;
  padding: 0 !important;
}

.job-dossier--modern .jd-stepper .v-stepper__content {
  border-top: 1px solid var(--jd-border);
}

/* Pricing bar on top, lane card below at full width. */
.job-dossier--modern .jd-lane-workbench {
  align-items: start;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(0, 1fr);
  padding: 1rem;
  position: relative;
}

.job-dossier--modern .jd-lane-workbench__main,
.job-dossier--modern .jd-lane-workbench__quote,
.job-dossier--modern .jd-assignments-panel {
  min-width: 0;
}

.job-dossier--modern .jd-lane-workbench__quote {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  height: auto;
  order: -1;
  overflow: visible !important;
  position: static;
  width: 100%;
}

.job-dossier--modern .jd-lane-workbench__quote > .quote-compact,
.job-dossier--modern .jd-lane-workbench__quote .quote-compact__card,
.job-dossier--modern .jd-lane-workbench__quote .quote-compact__body {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  height: auto !important;
  min-height: 0 !important;
}

.job-dossier--modern .jd-lane-workbench__main {
  width: 100%;
}

.job-dossier--modern .lane-ops {
  --lane-bg: #f7f3ec;
  --lane-panel: #ffffff;
  --lane-border: #e7e0d6;
  --lane-ink: #171b26;
  --lane-muted: #777166;
  --lane-soft: #fff3e6;
  --lane-orange: #f58220;
  --lane-orange-dark: #b85d0d;
  --lane-green: #11875d;
}

.job-dossier--modern .lane-ops .lane-card,
.job-dossier--modern .quote-compact__card {
  background: #ffffff !important;
  border: 1px solid var(--jd-border) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  overflow: hidden;
}

.job-dossier--modern .jd-lane-workbench__quote .quote-compact__card {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  overflow: visible !important;
}

.job-dossier--modern .lane-ops .lane-header,
.job-dossier--modern .lane-ops .lane-route-vector,
.job-dossier--modern .lane-ops .lane-action-bar {
  border-color: var(--jd-border) !important;
}

.job-dossier--modern .lane-ops .lane-header {
  background: #ffffff;
  padding: 0.875rem 1rem;
}

.job-dossier--modern .lane-ops .lane-kicker span {
  color: var(--jd-orange-dark);
}

.job-dossier--modern .lane-ops .lane-header h3 {
  color: var(--jd-ink);
  font-size: 1rem;
}

.job-dossier--modern .lane-ops .lane-route-vector {
  background: #fbfaf7;
  padding: 0.875rem 1rem 1.35rem;
}

.job-dossier--modern .lane-ops .lane-endpoint {
  border-color: var(--jd-border);
  padding: 0.875rem 1rem 0.5rem;
}

.job-dossier--modern .lane-ops .lane-endpoint__marker {
  background: var(--jd-soft);
  color: var(--jd-orange-dark) !important;
}

.job-dossier--modern .lane-ops .lane-endpoint--destination .lane-endpoint__marker {
  background: #e9f8ef;
  color: #11875d !important;
}

.job-dossier--modern .lane-ops .lane-field-grid {
  gap: 0.5rem;
}

.job-dossier--modern .lane-ops .lane-field-grid .lane-field:first-child {
  grid-column: 1 / -1;
}

.job-dossier--modern .lane-ops .lane-field-grid .v-text-field > .v-input__control > .v-input__slot,
.job-dossier--modern .lane-ops .lane-field-grid .v-select > .v-input__control > .v-input__slot {
  background: #fbfaf7;
  border: 1px solid #e9e1d7;
  border-radius: 7px;
  min-height: 2.375rem;
  padding: 0 0.625rem;
}

.job-dossier--modern .lane-ops .lane-field-grid .v-input__slot,
.job-dossier--modern .lane-ops .lane-field-grid .v-input__control,
.job-dossier--modern .quote-compact .v-input__slot,
.job-dossier--modern .quote-compact .v-input__control {
  transition: none !important;
}

.job-dossier--modern .lane-ops .lane-field-grid .v-input input,
.job-dossier--modern .lane-ops .lane-field-grid .v-select__selection,
.job-dossier--modern .quote-compact .v-input input,
.job-dossier--modern .quote-compact .v-select__selection {
  max-width: 100%;
  min-width: 0;
}

.job-dossier--modern .lane-ops .lane-field-grid .lane-input--dense-text input,
.job-dossier--modern .lane-ops .lane-field-grid .lane-input--dense-text .v-select__selection {
  font-size: 0.75rem !important;
}

.job-dossier--modern .lane-ops .lane-field-grid .lane-input--tight-text input,
.job-dossier--modern .lane-ops .lane-field-grid .lane-input--tight-text .v-select__selection {
  font-size: 0.6875rem !important;
}

.job-dossier--modern .lane-ops .lane-field-grid .v-text-field > .v-input__control > .v-input__slot::before,
.job-dossier--modern .lane-ops .lane-field-grid .v-text-field > .v-input__control > .v-input__slot::after,
.job-dossier--modern .lane-ops .lane-field-grid .v-select > .v-input__control > .v-input__slot::before,
.job-dossier--modern .lane-ops .lane-field-grid .v-select > .v-input__control > .v-input__slot::after,
.job-dossier--modern .quote-compact .v-text-field > .v-input__control > .v-input__slot::before,
.job-dossier--modern .quote-compact .v-text-field > .v-input__control > .v-input__slot::after {
  display: none;
}

.job-dossier--modern .lane-ops .lane-action-bar {
  background: #ffffff;
  padding: 0.75rem 1rem;
}

.job-dossier--modern .lane-ops .lane-action-bar .v-btn,
.job-dossier--modern .quote-compact__driver-edit-btn {
  border-radius: 7px !important;
  box-shadow: none !important;
}

.job-dossier--modern .lane-ops .lane-action-bar .v-btn,
.job-dossier--modern .quote-compact__driver-edit-btn,
.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools button,
.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .v-btn,
.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .commentbtn,
.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .oms-comment-plus-btn,
.job-dossier--modern .job-detail-profile-card .job-info-panel .track-ass-btn,
.job-dossier--modern .job-detail-profile-card .job-info-panel .orange-button,
.job-dossier--modern .job-detail-profile-card .job-info-panel button,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-btn {
  background: var(--jd-orange) !important;
  border: 1px solid var(--jd-orange) !important;
  color: #ffffff !important;
}

.job-dossier--modern .lane-ops .lane-action-bar .v-btn .v-btn__content,
.job-dossier--modern .lane-ops .lane-action-bar .v-btn .v-icon,
.job-dossier--modern .quote-compact__driver-edit-btn .v-icon,
.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools button *,
.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .v-btn *,
.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .commentbtn *,
.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .oms-comment-plus-btn *,
.job-dossier--modern .job-detail-profile-card .job-info-panel button *,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-btn * {
  color: #ffffff !important;
}

.job-dossier--modern .lane-ops .lane-action-bar .v-btn:hover:not(.v-btn--disabled),
.job-dossier--modern .quote-compact__driver-edit-btn:hover:not([disabled]),
.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools button:hover:not([disabled]),
.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .v-btn:hover:not(.v-btn--disabled),
.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .commentbtn:hover,
.job-dossier--modern .job-detail-profile-card .job-info-panel button:hover:not([disabled]),
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-btn:hover:not(.v-btn--disabled) {
  background: #d96f15 !important;
  border-color: #d96f15 !important;
  color: #ffffff !important;
}

.job-dossier--modern .lane-ops .lane-action-bar .v-btn.v-btn--disabled,
.job-dossier--modern .quote-compact__driver-edit-btn[disabled],
.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools button[disabled],
.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .v-btn.v-btn--disabled,
.job-dossier--modern .job-detail-profile-card .job-info-panel button[disabled],
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-btn.v-btn--disabled {
  background: #e4e7ec !important;
  border-color: #e4e7ec !important;
  color: #98a2b3 !important;
}

.job-dossier.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .job-profile-action-item .commentbtn,
.job-dossier.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .job-profile-action-item .v-btn,
.job-dossier.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .job-profile-action-item .oms-comment-plus-btn {
  background: var(--jd-orange) !important;
  border-color: var(--jd-orange) !important;
  color: #ffffff !important;
}

.job-dossier.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .job-profile-action-item .commentbtn *,
.job-dossier.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .job-profile-action-item .v-btn *,
.job-dossier.job-dossier--modern .job-detail-profile-card .job-profile-actions__tools .job-profile-action-item .oms-comment-plus-btn * {
  color: #ffffff !important;
}

.job-dossier--modern .quote-compact__body {
  padding: 0 !important;
}

.job-dossier--modern .quote-compact__title,
.job-dossier--modern .quote-compact__body .v-divider {
  display: none !important;
}

.job-dossier--modern .quote-compact__grid {
  display: flex !important;
  flex-direction: column;
  gap: 0.5rem;
  grid-template-columns: none;
  padding: 0 !important;
}

.job-dossier--modern .quote-compact__field {
  background: transparent;
  border: 0;
  border-radius: 0;
  padding: 0;
}

.job-dossier--modern .quote-compact__input-row {
  align-items: center;
  display: grid;
  gap: 0.45rem;
  grid-template-columns: minmax(0, 1fr) 2.25rem;
}

.job-dossier--modern .quote-compact__edit {
  align-self: center;
  display: flex;
  padding-top: 0 !important;
}

.job-dossier--modern .quote-compact__driver-edit-btn {
  align-items: center !important;
  background: var(--jd-orange, #f58220) !important;
  border: 1px solid var(--jd-orange, #f58220) !important;
  border-radius: 0.45rem !important;
  box-shadow: none !important;
  color: #ffffff !important;
  display: inline-flex !important;
  height: 2.25rem !important;
  justify-content: center !important;
  min-height: 2.25rem !important;
  min-width: 2.25rem !important;
  padding: 0 !important;
  width: 2.25rem !important;
}

.job-dossier--modern .quote-compact__driver-edit-btn .v-icon {
  color: #ffffff !important;
  font-size: 1rem !important;
}

.job-dossier--modern .quote-compact .v-text-field > .v-input__control > .v-input__slot,
.job-dossier--modern .quote-compact .v-select > .v-input__control > .v-input__slot {
  align-items: flex-start !important;
  background: #ffffff !important;
  border: 1px solid #d8dde6 !important;
  border-radius: 0.45rem !important;
  min-height: 3.5rem !important;
  padding: 0.7rem 0.75rem 0.45rem !important;
}

.job-dossier--modern .quote-compact__field--driver {
  margin-bottom: 0.75rem;
}

.job-dossier--modern .quote-compact__field--driver .v-text-field > .v-input__control > .v-input__slot {
  min-height: 3.15rem !important;
  padding: 0.55rem 0.75rem 0.35rem !important;
}

.job-dossier--modern .quote-compact .v-text-field__slot,
.job-dossier--modern .quote-compact .v-select__slot {
  align-items: flex-start !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: flex-start !important;
  min-height: 2.35rem;
  position: relative !important;
  width: 100%;
}

.job-dossier--modern .quote-compact .v-label {
  background: transparent !important;
  color: #8a857c !important;
  font-size: 0.6875rem !important;
  font-weight: 800 !important;
  height: auto !important;
  left: 0 !important;
  letter-spacing: 0.01em;
  line-height: 1.2 !important;
  margin: 0 0 0.35rem !important;
  max-width: 100% !important;
  padding: 0.05rem 0 0 !important;
  pointer-events: none;
  position: relative !important;
  top: 0 !important;
  transform: none !important;
  white-space: nowrap;
  -webkit-text-fill-color: #8a857c !important;
}

.job-dossier--modern .quote-compact .v-label--active,
.job-dossier--modern .quote-compact .v-input--is-label-active .v-label,
.job-dossier--modern .quote-compact .v-input--is-dirty .v-label {
  transform: none !important;
}

.job-dossier--modern .quote-compact .v-input input,
.job-dossier--modern .quote-compact .v-select__selection {
  color: #171b26 !important;
  font-size: 0.9375rem !important;
  font-weight: 800 !important;
  line-height: 1.25 !important;
  margin-top: 0.15rem !important;
  padding: 0 !important;
  -webkit-text-fill-color: #171b26 !important;
}

.job-dossier--modern .quote-compact .v-input--is-disabled .v-input__slot {
  background: #ffffff !important;
  opacity: 1 !important;
}

.job-dossier--modern .quote-compact .v-input--is-disabled input,
.job-dossier--modern .quote-compact .v-input--is-disabled .v-select__selection {
  color: #171b26 !important;
  opacity: 1 !important;
  -webkit-text-fill-color: #171b26 !important;
}

.job-dossier--modern .quote-compact,
.job-dossier--modern .quote-compact .v-input,
.job-dossier--modern .quote-compact .v-input__control,
.job-dossier--modern .quote-compact .v-input__slot {
  cursor: default;
}

.job-dossier--modern .quote-compact .v-input:not(.v-input--is-disabled) input {
  cursor: text !important;
}

.job-dossier--modern .quote-compact .v-input--is-disabled,
.job-dossier--modern .quote-compact .v-input--is-disabled * {
  cursor: default !important;
}

.job-dossier--modern .quote-compact__driver-edit-btn:not([disabled]) {
  cursor: pointer !important;
}

.job-dossier--modern .job-detail-profile-card .job-info-panel,
.job-dossier--modern .job-detail-profile-card .job-info-panel > .layout,
.job-dossier--modern .job-detail-profile-card .job-info-panel > .layout > .flex,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-form > .layout,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-form > .layout > .flex,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-input,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-input__control,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-input__slot,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-text-field__slot,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-select__slot,
.job-dossier--modern .quote-compact__field,
.job-dossier--modern .quote-compact__input-row,
.job-dossier--modern .quote-compact .v-input,
.job-dossier--modern .quote-compact .v-input__control,
.job-dossier--modern .quote-compact .v-input__slot,
.job-dossier--modern .lane-ops .lane-field,
.job-dossier--modern .lane-ops .lane-field-grid .v-input,
.job-dossier--modern .lane-ops .lane-field-grid .v-input__control,
.job-dossier--modern .lane-ops .lane-field-grid .v-input__slot {
  transition: none !important;
}

.job-dossier--modern .job-detail-profile-card .job-info-panel .v-input__slot::before,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-input__slot::after,
.job-dossier--modern .quote-compact .v-input__slot::before,
.job-dossier--modern .quote-compact .v-input__slot::after,
.job-dossier--modern .lane-ops .lane-field-grid .v-input__slot::before,
.job-dossier--modern .lane-ops .lane-field-grid .v-input__slot::after {
  border: 0 !important;
  display: none !important;
  opacity: 0 !important;
  transition: none !important;
}

.job-dossier--modern .job-detail-profile-card .job-info-panel,
.job-dossier--modern .job-detail-profile-card .job-info-panel .heading,
.job-dossier--modern .job-detail-profile-card .job-info-panel .subheading,
.job-dossier--modern .job-detail-profile-card .job-info-panel .muted,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-input,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-input__control,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-input__slot,
.job-dossier--modern .quote-compact,
.job-dossier--modern .quote-compact__field,
.job-dossier--modern .quote-compact .v-input,
.job-dossier--modern .quote-compact .v-input__control,
.job-dossier--modern .quote-compact .v-input__slot,
.job-dossier--modern .lane-ops .lane-field,
.job-dossier--modern .lane-ops .lane-field-grid .v-input,
.job-dossier--modern .lane-ops .lane-field-grid .v-input__control,
.job-dossier--modern .lane-ops .lane-field-grid .v-input__slot {
  cursor: default !important;
}

.job-dossier--modern .job-detail-profile-card .job-info-panel *,
.job-dossier--modern .quote-compact__field *,
.job-dossier--modern .lane-ops .lane-field-grid * {
  cursor: inherit !important;
}

.job-dossier--modern .job-detail-profile-card .job-info-panel .v-input--is-disabled input,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-input--is-disabled textarea,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-input--is-disabled .v-select__selection,
.job-dossier--modern .quote-compact .v-input--is-disabled input,
.job-dossier--modern .quote-compact .v-input--is-disabled textarea,
.job-dossier--modern .quote-compact .v-input--is-disabled .v-select__selection,
.job-dossier--modern .lane-ops .lane-field-grid .v-input--is-disabled input,
.job-dossier--modern .lane-ops .lane-field-grid .v-input--is-disabled textarea,
.job-dossier--modern .lane-ops .lane-field-grid .v-input--is-disabled .v-select__selection {
  caret-color: transparent !important;
  cursor: default !important;
  pointer-events: none;
}

.job-dossier--modern .job-detail-profile-card .job-info-panel .v-input:not(.v-input--is-disabled) input,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-input:not(.v-input--is-disabled) textarea,
.job-dossier--modern .quote-compact .v-input:not(.v-input--is-disabled) input,
.job-dossier--modern .quote-compact .v-input:not(.v-input--is-disabled) textarea,
.job-dossier--modern .lane-ops .lane-field-grid .v-input:not(.v-input--is-disabled) input,
.job-dossier--modern .lane-ops .lane-field-grid .v-input:not(.v-input--is-disabled) textarea {
  cursor: text !important;
}

.job-dossier--modern .job-detail-profile-card .job-info-panel .v-select:not(.v-input--is-disabled),
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-select:not(.v-input--is-disabled) *,
.job-dossier--modern .job-detail-profile-card .payer-strip__radio:not(.v-input--is-disabled) .v-radio,
.job-dossier--modern .job-detail-profile-card .payer-strip__radio:not(.v-input--is-disabled) .v-radio *,
.job-dossier--modern .job-detail-profile-card .payer-strip__radio:not(.v-input--is-disabled) .v-label,
.job-dossier--modern .job-detail-profile-card .payer-strip__radio:not(.v-input--is-disabled) .v-input--selection-controls__input,
.job-dossier--modern .job-detail-profile-card .job-info-panel button:not([disabled]),
.job-dossier--modern .job-detail-profile-card .job-info-panel button:not([disabled]) *,
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-btn:not(.v-btn--disabled),
.job-dossier--modern .job-detail-profile-card .job-info-panel .v-btn:not(.v-btn--disabled) *,
.job-dossier--modern .quote-compact__driver-edit-btn:not([disabled]),
.job-dossier--modern .quote-compact__driver-edit-btn:not([disabled]) *,
.job-dossier--modern .lane-ops .lane-field-grid .v-select:not(.v-input--is-disabled),
.job-dossier--modern .lane-ops .lane-field-grid .v-select:not(.v-input--is-disabled) * {
  cursor: pointer !important;
}

.job-dossier--modern .jd-assignments-panel {
  border-top: 1px solid var(--jd-border);
  margin-top: 0;
  padding: 1rem;
}

.job-dossier--modern .assignments-modern-card {
  background: #ffffff !important;
  border: 1px solid var(--jd-border) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  overflow: hidden;
}

.job-dossier--modern .assignments-modern-card > .v-card__title.bg-clr {
  background: #ffffff !important;
  border-bottom: 1px solid var(--jd-border);
  min-height: 3.25rem;
  padding: 0.75rem 1rem !important;
}

.job-dossier--modern .assignments-modern-card > .v-card__title .layout {
  align-items: center;
}

.job-dossier--modern .assignments-modern-card > .v-card__title .heading {
  color: var(--jd-ink);
  font-size: 0.9375rem;
  font-weight: 900;
  padding: 0 !important;
}

.job-dossier--modern .assignments-table-shell {
  background: #ffffff;
  padding: 0 !important;
}

.job-dossier--modern .assignments-modern-table .v-table__overflow {
  border: 0;
  border-radius: 0;
}

.job-dossier--modern .assignments-modern-table table.v-table {
  border-collapse: separate;
  border-spacing: 0;
}

.job-dossier--modern .assignments-modern-table table.v-table thead th {
  background: #fbfaf7;
  border-bottom: 1px solid var(--jd-border) !important;
  color: #777166 !important;
  font-size: 0.6875rem !important;
  font-weight: 900 !important;
  height: 2.75rem;
  text-transform: uppercase;
}

.job-dossier--modern .assignments-modern-table table.v-table tbody td {
  border-bottom: 1px solid #eee7dd !important;
  color: #202534;
  font-size: 0.75rem;
  height: 3.25rem;
}

.job-dossier--modern .assignments-modern-table .assignment-row:hover {
  background: #fffaf5 !important;
}

.job-dossier--modern .assignments-modern-table .assignment-row--active {
  background: #fff3e6 !important;
}

.job-dossier--modern .assignment-focus-shell {
  background: #fbfaf7;
  border: 1px solid var(--jd-border);
  border-radius: 8px;
  box-shadow: none;
  margin: 0.75rem;
  padding: 0.875rem;
}

.job-dossier--modern .assignment-focus-header {
  background: #ffffff;
  border: 1px solid #eee7dd;
  border-radius: 8px;
  margin-bottom: 0.875rem;
  padding: 0.75rem;
}

.job-dossier--modern .assignment-focus-header span {
  color: var(--jd-muted);
}

.job-dossier--modern .assignment-focus-header strong {
  color: var(--jd-ink);
}

@media (max-width: 1180px) {
  .job-dossier--modern .jd-lane-workbench {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1100px) {
  .job-dossier--modern .jd-lane-workbench {
    grid-template-columns: minmax(0, 1fr);
  }

  .job-dossier--modern .jd-lane-workbench__quote {
    max-width: none;
    width: 100%;
  }

  .job-dossier--modern .quote-compact__grid {
    display: grid !important;
    gap: 0.65rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .job-dossier--modern .quote-compact__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 860px) {
  .job-dossier--modern.job-dossier {
    padding: 0.5rem !important;
  }

  .job-dossier--modern .jd-command__nav--flat {
    align-items: flex-start;
  }

  .job-dossier--modern .jd-job-strip {
    border-radius: 8px;
    margin-left: 0;
    width: 100%;
  }

  .job-dossier--modern .jd-modern-workspace,
  .job-dossier--modern .jd-command--flat {
    width: 100%;
  }

  .job-dossier--modern .jd-lane-tabs {
    overflow-x: auto;
  }

  .job-dossier--modern .lane-ops .lane-route-vector,
  .job-dossier--modern .lane-ops .lane-endpoints {
    grid-template-columns: 1fr;
  }

  .job-dossier--modern .lane-ops .lane-route-vector__node--end {
    text-align: left;
  }

  .job-dossier--modern .lane-ops .lane-route-vector__line {
    display: none;
  }
}
</style>

<style scoped>
.v-stepper__header {
  justify-content: left !important;
}
</style>
